import { useState, useEffect, useRef } from 'react';
import {
  changePassword, rootListUsers, rootAssignRole,
  rootRequestPaymentAuth, rootVerifyPaymentAuth, rootGetPaymentConfig, rootUpdatePaymentConfig,
  rootPurgeData, rootPurgeUsers, rootPurgeDatabase, rootBroadcast, rootDownloadReport, rootReverifyPayments,
  rootGetActivityLogs, rootGetFinanceDashboard,
  rootRequestActionAuth,
  rootDownloadFinancePdf, rootDownloadActivityPdf,
  deleteProduct, deleteCategory, saveProduct, saveCategory,
  fetchAdminOrders, updateOrderStatus, fetchAdminUsers, saveAdminUser, deleteAdminUser,
  fetchAdminCustomers, purgeAllOrders, fetchAdminProducts, fetchAdminBanners, saveBanner, deleteBanner,
  saveHeroSlide, deleteHeroSlide, fetchAdminHeroSlides,
  adminFetchChats, adminFetchChatThread, adminSendChatReply,
  rootExportDatabase, rootImportDatabase,
  fetchSettings, updateShippingSettings,
  fetchAdminNotifications, markAdminNotificationsRead
} from '../db';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from '../toast';
import ToastContainer from '../components/ToastContainer';
import ConfirmModal from '../components/ConfirmModal';


const ROLE_TABS = {
  root: [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'products', label: 'Products' },
    { id: 'categories', label: 'Categories' },
    { id: 'carousel', label: 'Hero Carousel' },
    { id: 'banners', label: 'Promo Banners' },
    { id: 'orders', label: 'Orders' },
    { id: 'income', label: 'Income Log' },
    { id: 'refunds', label: 'Refunds Log' },
    { id: 'users', label: 'Users' },
    { id: 'shipping', label: 'Shipping' },
    { id: 'root_cms', label: 'Root CMS' },
  ],
  superadmin: [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'products', label: 'Products' },
    { id: 'categories', label: 'Categories' },
    { id: 'carousel', label: 'Hero Carousel' },
    { id: 'banners', label: 'Promo Banners' },
    { id: 'orders', label: 'Orders' },
    { id: 'income', label: 'Income Log' },
    { id: 'refunds', label: 'Refunds Log' },
    { id: 'customers', label: 'Customers' },
    { id: 'chats', label: 'Customer Chats' },
    { id: 'users', label: 'Admin Users' },
    { id: 'shipping', label: 'Shipping' },
  ],
  manager: [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'products', label: 'Products' },
    { id: 'categories', label: 'Categories' },
    { id: 'banners', label: 'Promo Banners' },
    { id: 'orders', label: 'Orders', },
    { id: 'income', label: 'Income Log' },
    { id: 'refunds', label: 'Refunds Log' },
    { id: 'customers', label: 'Customers' },
    { id: 'chats', label: 'Customer Chats' },
  ],
  staff: [
    { id: 'orders', label: 'Orders', },
    { id: 'chats', label: 'Customer Chats' },
  ],
  inventory: [
    { id: 'products', label: 'Products' },
    { id: 'drafts', label: 'Drafts' },
  ],
};

// Matches the STATUS_MAP used on the customer-facing OrdersPage
const ORDER_STATUS_STYLES = {
  pending:    'bg-amber-50 text-amber-700 border border-amber-200',
  processing: 'bg-blue-50 text-blue-700 border border-blue-200',
  shipped:    'bg-violet-50 text-violet-700 border border-violet-200',
  delivered:  'bg-green-50 text-green-700 border border-green-200',
  cancelled:  'bg-red-50 text-red-600 border border-red-200',
  failed:     'bg-red-50 text-red-600 border border-red-200',
};
const PAYMENT_STATUS_STYLES = {
  paid:   'bg-emerald-50 text-emerald-700 border border-emerald-200',
  unpaid: 'bg-gray-50 text-gray-500 border border-gray-200',
  failed: 'bg-red-50 text-red-500 border border-red-200',
};

// eslint-disable-next-line no-unused-vars
const ROLE_LABELS = {
  superadmin: ' Super Admin',
  manager: ' Manager',
  staff: ' Sales Staff',
  inventory: ' Inventory',
};

export default function AdminPage({ onNavigate, onLogout, products, banners, heroSlides, categories, theme, refreshData, user: currentUser }) {
  const isRoot = currentUser?.role === 'root';
  const [currentRole, setCurrentRole] = useState(isRoot ? 'root' : 'superadmin');
  const [confirmModalState, setConfirmModalState] = useState({ isOpen: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null, isProcessing: false });

  // ── Force password change ─────────────────────────────────────────────────
  const [showChangePwd, setShowChangePwd]     = useState(currentUser?.must_change_password === true);

  useEffect(() => {
    if (currentUser) {
      setCurrentRole(currentUser.role === 'root' ? 'root' : (currentUser.role || 'superadmin'));
      setShowChangePwd(currentUser.must_change_password === true);
    }
  }, [currentUser]);
  const [changePwdForm, setChangePwdForm]     = useState({ password: '', confirm: '' });
  const [changePwdError, setChangePwdError]   = useState('');
  const [changePwdLoading, setChangePwdLoading] = useState(false);

  const handleForcedPwdChange = async (e) => {
    e.preventDefault();
    setChangePwdError('');
    if (changePwdForm.password.length < 8) { setChangePwdError('Password must be at least 8 characters.'); return; }
    if (changePwdForm.password !== changePwdForm.confirm) { setChangePwdError('Passwords do not match.'); return; }
    setChangePwdLoading(true);
    try {
      await changePassword(changePwdForm.password, changePwdForm.confirm);
      setShowChangePwd(false);
    } catch (err) {
      setChangePwdError(err?.response?.data?.message || 'Failed to change password.');
    } finally { setChangePwdLoading(false); }
  };

  // ── Root CMS state ────────────────────────────────────────────────────────
  const [rootUsers, setRootUsers]               = useState([]);
  const [rootPayConfig, setRootPayConfig]       = useState({ paystack_public_key: '', paystack_secret_key: '' });
  const [shippingConfig, setShippingConfig]     = useState({ shipping_enabled: true, shipping_fee_lagos_kwara: 2000, shipping_fee_other: 4000 });
  const [shippingSaving, setShippingSaving]     = useState(false);

  // ── Notifications ──────────────────────────────────────────────────────────
  const [notifications, setNotifications]       = useState([]);
  const [notifOpen, setNotifOpen]               = useState(false);
  const notifRef                                = useRef(null);
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const [rootMsg, setRootMsg]                   = useState({ subject: '', message: '' });
  const [rootLoading, setRootLoading]           = useState(false);
  const [rootNotice, setRootNotice]             = useState('');
  const [rootCmsTab, setRootCmsTab]             = useState('roles');

  const [paymentAuthToken, setPaymentAuthToken] = useState('');
  const [paymentAuthStep, setPaymentAuthStep]   = useState(0); // 0 = need password, 1 = need OTP, 2 = unlocked
  const [paymentAuthPassword, setPaymentAuthPassword] = useState('');
  const [paymentAuthOtp, setPaymentAuthOtp]     = useState('');

  const [rootActionAuthModal, setRootActionAuthModal] = useState({
    isOpen: false,
    step: 0, // 0 = password, 1 = otp
    password: '',
    otp: '',
    actionName: '',
    onSuccess: null,
    isProcessing: false,
    error: ''
  });

  const [rootActivityLogs, setRootActivityLogs] = useState([]);
  const [rootFinance, setRootFinance]           = useState(null);
  const [rootFinanceFilter, setRootFinanceFilter] = useState('all');

  useEffect(() => {
    if (isRoot && currentRole === 'root') {
      rootListUsers().then(setRootUsers).catch(() => {});
      rootGetActivityLogs().then(setRootActivityLogs).catch(() => {});
      rootGetFinanceDashboard().then(setRootFinance).catch(() => {});
    }
  }, [isRoot, currentRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll notifications every 30 seconds
  useEffect(() => {
    const loadNotifs = () => fetchAdminNotifications().then(setNotifications).catch(() => {});
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close notif dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doRootAction = async (fn, successMsg) => {
    setRootLoading(true); setRootNotice('');
    try { await fn(); setRootNotice(successMsg || 'Done!'); }
    catch (e) { setRootNotice('Error: ' + (e?.response?.data?.message || e.message)); }
    finally { setRootLoading(false); }
  };
  const defaultTab = ROLE_TABS[currentUser?.role]?.[0]?.id || 'dashboard';
  const [currentTab, setCurrentTab] = useState(defaultTab);
  
  useEffect(() => {
    if (currentUser?.role && currentTab === 'dashboard' && !ROLE_TABS[currentUser.role]?.find(t => t.id === 'dashboard')) {
        setCurrentTab(ROLE_TABS[currentUser.role][0].id);
    }
    fetchSettings().then(settings => {
      setShippingConfig({
        shipping_enabled: settings.shipping_enabled !== 'false',
        shipping_fee_lagos_kwara: Number(settings.shipping_fee_lagos_kwara) || 2000,
        shipping_fee_other: Number(settings.shipping_fee_other) || 4000
      });
    }).catch(() => {});
  }, [currentUser, currentTab]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
  const [dashboardOrderSearch, setDashboardOrderSearch] = useState('');
  const [dashboardStatusFilter, setDashboardStatusFilter] = useState('all');


  /* ── Carousel slide modal ── */
  const [isSlideModalOpen, setIsSlideModalOpen] = useState(false);
  const [isEditSlideOpen, setIsEditSlideOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [newSlide, setNewSlide] = useState({ imageFile: null, cta: '', dark: false });

  /* ── Admin-managed banners & slides (loaded directly, not from public prop) ── */
  const [adminBanners, setAdminBanners] = useState([]);
  const [adminHeroSlides, setAdminHeroSlides] = useState([]);

  /* ── Admin Products (all statuses incl. drafts) ── */
  const [adminProducts, setAdminProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const adminProductsRetryRef = useRef(null);

  /* ── Staff Users ── */
  const [users, setUsers] = useState([]);

  /* ── Orders ── */
  const [orders, setOrders] = useState([]);

  /* ── Chats ── */
  const [chatThreads, setChatThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [activeThreadMessages, setActiveThreadMessages] = useState([]);
  const [adminChatInput, setAdminChatInput] = useState('');
  const chatEndRef = useRef(null);

  /* ── Customers ── */
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');

  /* ── Modal States ── */
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const [isEditProductOpen, setIsEditProductOpen] = useState(false);
  const [isEditBannerOpen, setIsEditBannerOpen] = useState(false);
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  /* ── Form Data ── */
  const [newProduct, setNewProduct] = useState({ name: '', category_id: '', price: '', original_price: '', stock: '', status: 'active', description: '', image: '', images: '' });
  const [newBanner, setNewBanner] = useState({ title: '', subtitle: '', imageFile: null, start: '', end: '', status: 'active' });
  const [newCategory, setNewCategory] = useState({ name: '', imageFile: null });
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'manager' });

  /* ── Selected Items for editing/viewing ── */
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingUser, setEditingUser] = useState(null);



  /* ── Refresh admin banners and slides from server ── */
  const refreshAdminBannersSlides = async () => {
    try {
      const [b, s] = await Promise.all([
        fetchAdminBanners().catch(() => []),
        fetchAdminHeroSlides().catch(() => []),
      ]);
      setAdminBanners(Array.isArray(b) ? b : []);
      setAdminHeroSlides(Array.isArray(s) ? s : []);
    } catch (err) {
      console.error('Failed to load banners/slides', err);
    }
  };

  /* ── Load admin products independently (not blocked by orders/users) ── */
  const loadAdminProductsData = async () => {
    try {
      setProductsLoading(true);
      const fetched = await fetchAdminProducts();
      if (Array.isArray(fetched)) {
        setAdminProducts(fetched);
        // If still empty, schedule an aggressive retry in 3s
        if (fetched.length === 0) {
          if (adminProductsRetryRef.current) clearTimeout(adminProductsRetryRef.current);
          adminProductsRetryRef.current = setTimeout(loadAdminProductsData, 3000);
        }
      }
    } catch (e) {
      console.error('products err:', e);
      // Retry after 3s on error
      if (adminProductsRetryRef.current) clearTimeout(adminProductsRetryRef.current);
      adminProductsRetryRef.current = setTimeout(loadAdminProductsData, 3000);
    } finally {
      setProductsLoading(false);
    }
  };

  /* ── Load admin data on mount ── */
  useEffect(() => {
    const loadAdminData = async () => {
      try {
        const [fetchedOrders, fetchedUsers, fetchedCustomers] = await Promise.all([
          fetchAdminOrders().catch(e => { console.error('orders err:', e); return []; }),
          fetchAdminUsers().catch(e => { console.error('users err:', e); return []; }),
          fetchAdminCustomers().catch(e => { console.error('customers err:', e); return []; }),
        ]);
        if (Array.isArray(fetchedOrders)) {
          setOrders(fetchedOrders.map(o => ({
            id: o.reference || `#${o.id}`,
            dbId: o.id,
            userId: o.user_id || o.user?.id || null,
            customer: o.customer_name || o.user?.name || 'Unknown',
            email: o.customer_email || o.user?.email || '',
            phone: o.customer_phone || o.user?.phone || '',
            product: o.items?.map(i => i.product_name || i.product?.name || 'Item').join(', ') || '',
            items: o.items || [],
            amount: `₦${Number(o.total || 0).toLocaleString()}`,
            price: o.total || 0,
            subtotal: o.subtotal || 0,
            shipping_fee: o.shipping_fee || 0,
            date: new Date(o.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' + new Date(o.created_at).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' }),
            status: o.status || 'pending',
            paymentStatus: o.payment_status || 'unpaid',
            address: o.delivery_address?.shipping_address || o.shipping_address || '',
            notes: o.notes || '',
            status_history: o.status_history || [],
          })));
        }
        if (Array.isArray(fetchedUsers)) {
          setUsers(fetchedUsers.map(u => ({
            id: u.id,
            name: u.name,
            email: u.email,
            role: u.role || 'staff',
            lastActive: u.updated_at ? new Date(u.updated_at).toLocaleDateString() : 'N/A',
            disabled: u.disabled || false,
          })));
        }
        if (Array.isArray(fetchedCustomers)) {
          setCustomers(fetchedCustomers);
        }
      } catch (err) {
        console.error('Failed to load admin data', err);
      }
    };
    // Load products independently so orders/users slowness doesn't block them
    loadAdminProductsData();
    loadAdminData();
    refreshAdminBannersSlides();
    const interval = setInterval(loadAdminData, 8000);
    // Refresh products every 30s (lighter poll since it has its own retry logic)
    const productsInterval = setInterval(loadAdminProductsData, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(productsInterval);
      if (adminProductsRetryRef.current) clearTimeout(adminProductsRetryRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Auto scroll chat ── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatThreads, selectedThreadId]);



  /* ── Chat ── */
  const loadChatConversations = async () => {
    try {
      const data = await adminFetchChats();
      // data: [{ u_id, s_id, user_name, user_email, message, created_at, ... }]
      const formatted = data.map(t => ({
        id: `${t.u_id}-${t.s_id}`,
        u_id: t.u_id,
        s_id: t.s_id,
        customerName: t.user_name || 'Guest Customer',
        time: t.created_at ? new Date(t.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
        lastMessage: t.message,
        unreadCount: t.unread_count || 0,
      }));
      setChatThreads(prev => {
        const kept = prev.filter(p => p.isDummy && !formatted.find(f => f.u_id === p.u_id));
        return [...kept, ...formatted];
      });
    } catch (e) {
      console.error('Failed to load chats', e);
    }
  };

  const loadActiveThread = async () => {
    if (!selectedThreadId) return;
    const thread = chatThreads.find(t => t.id === selectedThreadId);
    if (!thread) return;
    try {
      const msgs = await adminFetchChatThread(thread.u_id === 0 ? null : thread.u_id, thread.s_id);
      setActiveThreadMessages(msgs);
    } catch (e) {
      console.error('Failed to load thread', e);
    }
  };

  useEffect(() => {
    if (currentTab === 'chats') {
      loadChatConversations();
      const interval = setInterval(loadChatConversations, 5000);
      return () => clearInterval(interval);
    }
  }, [currentTab]);

  useEffect(() => {
    if (currentTab === 'chats' && selectedThreadId) {
      loadActiveThread();
      const interval = setInterval(loadActiveThread, 5000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, selectedThreadId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeThreadMessages]);

  const handleSendAdminMessage = async (e) => {
    e.preventDefault();
    if (!adminChatInput.trim() || !selectedThreadId) return;
    
    const thread = chatThreads.find(t => t.id === selectedThreadId);
    if (!thread) return;

    const msgText = adminChatInput;
    setAdminChatInput('');
    setActiveThreadMessages(prev => [...prev, { is_admin_reply: true, message: msgText, created_at: new Date().toISOString() }]);

    try {
      await adminSendChatReply(msgText, thread.u_id === 0 ? null : thread.u_id, thread.s_id);
      loadActiveThread();
      loadChatConversations();
    } catch (err) {
      console.error('Failed to send admin reply', err);
      toast.error(err.message || 'Failed to send message');
    }
  };

  const handleChatCustomer = (order) => {
    if (!order.userId) {
      toast.error('Cannot chat with this customer (no user ID found).');
      return;
    }
    const existingThread = chatThreads.find(t => String(t.u_id) === String(order.userId));
    if (existingThread) {
      setSelectedThreadId(existingThread.id);
    } else {
      const dummyId = `${order.userId}-`;
      setChatThreads(prev => {
        if (!prev.find(t => t.id === dummyId)) {
          return [{
            id: dummyId, u_id: order.userId, s_id: '', customerName: order.customer || 'Customer',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            lastMessage: 'Started new chat...',
            isDummy: true
          }, ...prev];
        }
        return prev;
      });
      setSelectedThreadId(dummyId);
    }
    setCurrentTab('chats');
    setIsOrderModalOpen(false);
  };

  const [isSavingSlide, setIsSavingSlide] = useState(false);
  const [isSavingBanner, setIsSavingBanner] = useState(false);

  /* ── Slide helpers ── */
  const handleAddSlide = async () => {
    if (!newSlide.imageFile && !newSlide.image_url) return toast.error('Please upload an image');
    setIsSavingSlide(true);
    try {
      await saveHeroSlide(newSlide);
      await refreshAdminBannersSlides();
      if (typeof refreshData === 'function') refreshData();
      setIsSlideModalOpen(false);
      setNewSlide({ title: '', subtitle: '', image_url: '', imageFile: null, badge: '', cta: '', dark: false });
      toast.success('Slide added!');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to save slide');
    } finally {
      setIsSavingSlide(false);
    }
  };

  const handleUpdateSlide = async () => {
    if (!editingSlide) return;
    setIsSavingSlide(true);
    try {
      await saveHeroSlide(editingSlide, editingSlide.id);
      await refreshAdminBannersSlides();
      if (typeof refreshData === 'function') refreshData();
      setIsEditSlideOpen(false);
      setEditingSlide(null);
      toast.success('Slide updated!');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to update slide');
    } finally {
      setIsSavingSlide(false);
    }
  };
  const handleDeleteSlide = async (id) => {
    try { await deleteHeroSlide(id); await refreshAdminBannersSlides(); if (typeof refreshData === 'function') refreshData(); toast.success('Slide deleted'); } catch (e) { toast.error(e.message || 'Failed to delete slide'); }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name) { toast.error('Product name is required'); return; }
    if (!newProduct.price) { toast.error('Price is required'); return; }
    setIsSavingProduct(true);
    try {
      const payload = { ...newProduct };
      if (!payload.stock) payload.stock = 0;
      if (!payload.category_id) delete payload.category_id;
      await saveProduct(payload);
      refreshData();
      const refreshed = await fetchAdminProducts();
      setAdminProducts(refreshed);
      setIsProductModalOpen(false);
      setNewProduct({ name: '', category_id: '', price: '', original_price: '', stock: '', status: 'active', description: '', image: '', images: '' });
      toast.success('Product saved!');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join('. ') || e.message || 'Failed to save product');
    } finally {
      setIsSavingProduct(false);
    }
  };
  const handleUpdateProduct = async () => {
    if (!editingProduct?.name) { toast.error('Product name is required'); return; }
    if (!editingProduct?.price) { toast.error('Price is required'); return; }
    setIsSavingProduct(true);
    try {
      const payload = { ...editingProduct };
      if (!payload.stock) payload.stock = 0;
      if (!payload.category_id) delete payload.category_id;
      await saveProduct(payload, editingProduct.id);
      refreshData();
      const refreshed = await fetchAdminProducts();
      setAdminProducts(refreshed);
      setIsEditProductOpen(false);
      setEditingProduct(null);
      toast.success('Product updated!');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join('. ') || e.message || 'Failed to update product');
    } finally {
      setIsSavingProduct(false);
    }
  };
  const handleDeleteProduct = (id) => {
    setConfirmModalState({
      isOpen: true,
      title: 'Delete Product',
      message: 'Are you sure you want to delete this product? This action cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await deleteProduct(id);
          refreshData();
          const refreshed = await fetchAdminProducts();
          setAdminProducts(refreshed);
          toast.success('Product deleted');
        } catch (e) {
          toast.error('Failed to delete product');
        }
      }
    });
  };

  /* ── Banners ── */
  const handleAddBanner = async () => {
    if (!newBanner.title) {
      toast.error('Please enter a banner title');
      return;
    }
    setIsSavingBanner(true);
    try {
      await saveBanner(newBanner);
      await refreshAdminBannersSlides();
      if (typeof refreshData === 'function') refreshData();
      setIsBannerModalOpen(false);
      setNewBanner({ title: '', subtitle: '', imageFile: null, start: '', end: '', status: 'active' });
      toast.success('Banner added!');
    } catch (e) {
      console.error('Banner save error:', e);
      let msg = 'Failed to save banner';
      if (e.code === 'ECONNABORTED' || e.message?.toLowerCase().includes('timeout')) {
        msg = 'Upload timed out — the image may be too large or the server is busy. Try again.';
      } else if (e.response?.data?.errors) {
        msg = Object.values(e.response.data.errors).flat().join('. ');
      } else if (e.response?.data?.message) {
        msg = e.response.data.message;
      } else if (e.message) {
        msg = e.message;
      }
      toast.error(msg);
    } finally {
      setIsSavingBanner(false);
    }
  };
  const handleUpdateBanner = async () => {
    if (!editingBanner?.title) {
      toast.error('Please enter a banner title');
      return;
    }
    setIsSavingBanner(true);
    try {
      await saveBanner(editingBanner, editingBanner.id);
      await refreshAdminBannersSlides();
      if (typeof refreshData === 'function') refreshData();
      setIsEditBannerOpen(false);
      setEditingBanner(null);
      toast.success('Banner updated!');
    } catch (e) {
      console.error('Banner update error:', e);
      let msg = 'Failed to update banner';
      if (e.code === 'ECONNABORTED' || e.message?.toLowerCase().includes('timeout')) {
        msg = 'Upload timed out — the image may be too large or the server is busy. Try again.';
      } else if (e.response?.data?.errors) {
        msg = Object.values(e.response.data.errors).flat().join('. ');
      } else if (e.response?.data?.message) {
        msg = e.response.data.message;
      } else if (e.message) {
        msg = e.message;
      }
      toast.error(msg);
    } finally {
      setIsSavingBanner(false);
    }
  };
  const handleDeleteBanner = async (id) => {
    try { await deleteBanner(id); await refreshAdminBannersSlides(); if (typeof refreshData === 'function') refreshData(); toast.success('Banner deleted'); } catch (e) { toast.error('Failed to delete banner'); }
  };

  /* ── Categories ── */
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const handleAddCategory = async () => {
    if (!newCategory.name) return toast.error('Name is required');
    setIsSavingCategory(true);
    try {
      await saveCategory(newCategory);
      setIsCategoryModalOpen(false);
      setNewCategory({ name: '', imageFile: null });
      toast.success('Category created!');
      if (typeof refreshData === 'function') refreshData();
    } catch (e) {
      toast.error('Failed to create category');
    } finally {
      setIsSavingCategory(false);
    }
  };
  const handleUpdateCategory = async () => {
    if (!editingCategory.name) return toast.error('Name is required');
    setIsSavingCategory(true);
    try {
      await saveCategory(editingCategory, editingCategory.id);
      setIsEditCategoryOpen(false);
      setEditingCategory(null);
      toast.success('Category updated!');
      if (typeof refreshData === 'function') refreshData();
    } catch (e) {
      toast.error('Failed to update category');
    } finally {
      setIsSavingCategory(false);
    }
  };
  const handleDeleteCategory = async (id) => {
    try {
      await deleteCategory(id);
      toast.success('Category deleted');
      if (typeof refreshData === 'function') refreshData();
    } catch (e) {
      toast.error('Failed to delete category');
    }
  };

  /* ── Orders ── */
  const handleUpdateOrder = async () => {
    if (!selectedOrder) return;
    setIsUpdatingOrder(true);
    try {
      const payload = {
        status: selectedOrder.status,
        courier_name: selectedOrder.courier_name,
        tracking_number: selectedOrder.tracking_number,
        tracking_url: selectedOrder.tracking_url,
      };
      if (selectedOrder.status === 'picked_up' && selectedOrder.pickup_info) {
        payload.pickup_info = selectedOrder.pickup_info;
      }
      const updated = await updateOrderStatus(selectedOrder.dbId, payload);
      // Update local state from the server response
      setOrders(orders.map(o => o.dbId === selectedOrder.dbId ? { ...o, status: updated.status || selectedOrder.status, paymentStatus: updated.payment_status || o.paymentStatus } : o));
      setIsOrderModalOpen(false);
      setSelectedOrder(null);
      if (typeof refreshData === 'function') refreshData();
      toast.success('Order status updated successfully!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update order status: ' + (e.response?.data?.message || e.message));
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  const handleAddUser = async () => {
    setIsSendingInvite(true);
    try {
      await saveAdminUser(newUser);
      // refresh list from server
      const updated = await fetchAdminUsers();
      setUsers(updated);
      setIsUserModalOpen(false);
      setNewUser({ name: '', email: '', role: 'manager' });
      toast.success(`Invitation sent to ${newUser.email}. They will receive login credentials by email.`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to invite user: ' + (e.response?.data?.message || e.message));
    } finally {
      setIsSendingInvite(false);
    }
  };
  const handleUpdateUser = async () => {
    if (!editingUser?.name || !editingUser?.email) return;
    try {
      await saveAdminUser({ ...editingUser, _method: 'PUT', id: editingUser.id });
    } catch (_) {
      // If a dedicated update endpoint doesn't exist, optimistically update local state
    }
    const updated = await fetchAdminUsers().catch(() => null);
    if (updated) setUsers(updated);
    else setUsers(users.map(u => u.id === editingUser.id ? editingUser : u));
    setIsEditUserOpen(false);
    setEditingUser(null);
    toast.success('Staff member updated!');
  };
  const handleDeleteUser = (id) => {
    setConfirmModalState({
      isOpen: true,
      title: 'Delete Staff Member',
      message: 'Are you sure you want to delete this staff member?',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await deleteAdminUser(id);
          const updated = await fetchAdminUsers();
          setUsers(updated);
          toast.success('Staff member deleted');
        } catch (e) {
          console.error(e);
          toast.error(e.response?.data?.message || 'Failed to delete user');
        }
      }
    });
  };
  const toggleUserStatus = (id) => setUsers(users.map(u => u.id === id ? { ...u, disabled: !u.disabled } : u));

  const activeTabs = ROLE_TABS[currentRole] || [];

  /* ════════════════════════════════ RENDER ════════════════════════════════ */
  return (
    <div className="flex min-h-screen bg-[#F5F0EB] text-[#1A1A2E]">
      <ToastContainer />

      {/* MOBILE BACKDROP */}
      {isSidebarOpen && (
        <div onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-black/55 z-40 lg:hidden" />
      )}

      {/* ════ SIDEBAR ════ */}
      <aside className={`w-60 bg-white border-r border-[#E8E0D8] flex flex-col fixed top-0 left-0 bottom-0 z-50 transition-transform duration-300 lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-[#E8E0D8] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Manna Bridal" className="h-9 w-9 object-contain rounded-lg" />
            <div className="font-bold text-lg text-[#1A1A2E]">Manna <span className="text-[#F47B20]">Bridal</span></div>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-gray-700 font-bold text-lg">✕</button>
        </div>

        {/* Role Selector */}
        {!isRoot && (
          <div className="p-4 border-b border-[#E8E0D8] bg-[#FAF8F5]">
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Your Role</label>
            <span className="text-xs font-bold text-[#F47B20]">{currentUser?.role ? currentUser.role.toUpperCase() : 'STAFF'}</span>
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 pb-2">Menus</div>
          {activeTabs.map(tab => {
            let badgeCount = 0;
            if (tab.id === 'orders') {
              badgeCount = orders.filter(o => o.status === 'pending').length;
            } else if (tab.id === 'chats') {
              badgeCount = chatThreads.reduce((sum, t) => sum + (t.unreadCount || 0), 0);
            }

            return (
              <button
                key={tab.id}
                onClick={() => { setCurrentTab(tab.id); setIsSidebarOpen(false); }}
                className={`w-full flex justify-between items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${currentTab === tab.id ? 'bg-[#FEF0E6] text-[#F47B20]' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-3">
                  <span>{tab.icon}</span>{tab.label}
                </div>
                {badgeCount > 0 && (
                  <span className="bg-[#F47B20] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#E8E0D8]">
          <button onClick={() => { if (onLogout) onLogout(); else onNavigate('shop'); }} className="w-full py-2.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors">
            Log Out
          </button>
        </div>
      </aside>

      {/* ════ MAIN ════ */}
      <main className="lg:ml-60 flex-1 flex flex-col min-h-screen w-full overflow-x-hidden">

        {/* HEADER */}
        <header className="bg-white border-b border-[#E8E0D8] h-16 px-4 md:px-8 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 focus:outline-none">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="font-bold text-base md:text-lg text-gray-800 capitalize">{currentTab} Overview</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-2.5 py-1 bg-[#FEF0E6] text-[#F47B20] font-black rounded-full uppercase tracking-wider">{currentRole}</span>

            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={async () => {
                  setNotifOpen(v => !v);
                  if (!notifOpen && unreadCount > 0) {
                    await markAdminNotificationsRead();
                    setNotifications(n => n.map(x => ({ ...x, is_read: true })));
                  }
                }}
                className="relative w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center hover:bg-[#FEF0E6] transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[9px] font-black rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {notifOpen && (
                <div className="absolute right-0 top-11 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-bold text-sm text-[#1A1A2E]">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={async () => {
                          await markAdminNotificationsRead();
                          setNotifications(n => n.map(x => ({ ...x, is_read: true })));
                        }}
                        className="text-[10px] font-bold text-[#F47B20] hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-50">
                    {notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                        <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    ) : notifications.map(n => {
                      const iconMap = { order: { bg: 'bg-orange-50', text: 'text-[#F47B20]', emoji: '🛍️' }, refund: { bg: 'bg-rose-50', text: 'text-rose-500', emoji: '↩️' }, support: { bg: 'bg-blue-50', text: 'text-blue-500', emoji: '💬' } };
                      const style = iconMap[n.type] || { bg: 'bg-gray-50', text: 'text-gray-500', emoji: '🔔' };
                      const tabMap = { order: 'orders', refund: 'refunds', support: 'chats' };
                      const ago = (() => { const diff = Date.now() - new Date(n.created_at).getTime(); if (diff < 60000) return 'just now'; if (diff < 3600000) return Math.floor(diff/60000) + 'm ago'; if (diff < 86400000) return Math.floor(diff/3600000) + 'h ago'; return Math.floor(diff/86400000) + 'd ago'; })();
                      return (
                        <div
                          key={n.id}
                          onClick={() => { setCurrentTab(tabMap[n.type] || 'orders'); setNotifOpen(false); }}
                          className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${!n.is_read ? 'bg-orange-50/40' : ''}`}
                        >
                          <div className={`w-8 h-8 shrink-0 rounded-full ${style.bg} flex items-center justify-center text-sm`}>{style.emoji}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#1A1A2E] leading-snug">{n.message}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{ago}</p>
                          </div>
                          {!n.is_read && <div className="w-2 h-2 rounded-full bg-[#F47B20] shrink-0 mt-1.5" />}
                        </div>
                      );
                    })}
                  </div>

                  <div className="px-4 py-2.5 border-t border-gray-100 text-center">
                    <p className="text-[10px] text-gray-400">{notifications.length} notification{notifications.length !== 1 ? 's' : ''} total</p>
                  </div>
                </div>
              )}
            </div>

            <div className="w-8 h-8 rounded-full bg-[#F47B20] text-white flex items-center justify-center font-bold text-xs">M</div>
          </div>
        </header>

        <div className="p-4 md:p-8 flex-1 space-y-6">

          {/* ════ 1. DASHBOARD ════ */}
          {currentTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="cursor-pointer" onClick={() => setCurrentTab('products')}>
                  <StatCard label="Total Products" value={productsLoading && adminProducts.length === 0 ? '…' : adminProducts.length} />
                </div>
                <div className="cursor-pointer" onClick={() => setCurrentTab('banners')}>
                  <StatCard label="Active Promos" value={banners.length} />
                </div>
                <div className="cursor-pointer" onClick={() => setIsOrdersModalOpen(true)}>
                  <StatCard label="Total Orders" value={orders.length} />
                  <p className="text-[10px] text-gray-500 text-center mt-1">Click for breakdown</p>
                </div>
                {currentRole !== 'staff' ? (
                  <div className="cursor-pointer" onClick={() => setCurrentTab('income')}>
                    <StatCard label="Total Income" value={`₦${orders.filter(o => o.paymentStatus === 'paid' || o.status === 'delivered').reduce((sum, o) => sum + Number(o.price || 0), 0).toLocaleString()}`} highlight />
                    <p className="text-[10px] text-gray-500 text-center mt-1">Click for Income Log Sheet</p>
                  </div>
                ) : (
                  <div className="bg-white p-5 rounded-2xl border border-[#E8E0D8] opacity-50 select-none flex items-center gap-3">
                    <span className="text-xl"></span>
                    <div>
                      <div className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">Total Income</div>
                      <div className="text-sm font-bold text-gray-400">Restricted</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* ── 7-Day Orders Overview Chart ── */}
              <div className="bg-white p-6 rounded-3xl border border-[#E8E0D8] shadow-sm">
                <h3 className="text-sm font-bold text-[#1A1A2E] mb-6 uppercase tracking-wider">Orders Overview (Last 7 Days)</h3>
                {(() => {
                  const now = new Date();
                  now.setHours(0,0,0,0);
                  const days = [];
                  for(let i=6; i>=0; i--) {
                    const d = new Date(now);
                    d.setDate(d.getDate() - i);
                    days.push({
                      date: d,
                      label: d.toLocaleDateString('en-US', { weekday: 'short' }),
                      count: 0
                    });
                  }
                  orders.forEach(o => {
                    const d = new Date(o.created_at || o.date);
                    if(!isNaN(d.getTime())) {
                      d.setHours(0,0,0,0);
                      const diff = Math.floor((now - d) / (1000*60*60*24));
                      if(diff >= 0 && diff < 7) {
                        days[6 - diff].count++;
                      }
                    }
                  });
                  const maxCount = Math.max(...days.map(d => d.count), 5); // min height baseline

                  return (
                    <div className="flex items-end justify-between h-40 gap-2 sm:gap-4 mt-4">
                      {days.map((day, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full group">
                          <div className="w-full relative flex justify-center items-end h-full">
                            {day.count > 0 && (
                              <span className="absolute -top-6 text-[10px] font-bold text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">{day.count}</span>
                            )}
                            <div 
                              className="w-full max-w-[40px] bg-[#F47B20] rounded-t-lg transition-all duration-500 hover:bg-[#D96A10]" 
                              style={{ height: `${Math.max((day.count / maxCount) * 100, 2)}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-semibold text-gray-400 mt-3 uppercase tracking-wide">{day.label}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                <div className="px-5 py-3 border-b border-[#E8E0D8] flex items-center justify-between">
                  <h3 className="font-bold text-sm text-[#1A1A2E] uppercase tracking-wider">Recent Orders</h3>
                  <button
                    onClick={() => setCurrentTab('orders')}
                    className="text-xs font-bold text-[#F47B20] hover:underline"
                  >View all</button>
                </div>
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse min-w-[640px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        <th className="p-4">Order ID</th>
                        <th className="p-4">Customer</th>
                        <th className="p-4">Product</th>
                        {currentRole !== 'staff' && <th className="p-4">Amount</th>}
                        <th className="p-4">Payment</th>
                        <th className="p-4">Date</th>
                        <th className="p-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 10).map(o => {
                        const isCompleted = o.status === 'delivered' || o.status === 'cancelled';
                        return (
                          <tr
                            key={o.id}
                            onClick={() => { setSelectedOrder({ ...o }); setIsOrderModalOpen(true); }}
                            className={`border-b border-[#E8E0D8] text-sm cursor-pointer transition-colors ${isCompleted ? 'opacity-50 bg-gray-50/60 hover:bg-gray-100/60' : 'hover:bg-[#FEF0E6]/40'}`}
                          >
                            <td className={`p-4 font-bold ${isCompleted ? 'text-gray-400' : 'text-[#F47B20]'}`}>{o.id}</td>
                            <td className="p-4 text-gray-600">{o.customer}</td>
                            <td className="p-4 text-gray-600 max-w-[160px] truncate">{o.product}</td>
                            {currentRole !== 'staff' && <td className="p-4 font-semibold text-gray-700">{o.amount}</td>}
                            <td className="p-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${PAYMENT_STATUS_STYLES[o.paymentStatus] || 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                                {o.paymentStatus || 'unpaid'}
                              </span>
                            </td>
                            <td className="p-4 text-gray-400 text-xs whitespace-nowrap">{o.date}</td>
                            <td className="p-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${ORDER_STATUS_STYLES[o.status] || 'bg-gray-100 text-gray-500'}`}>
                                {o.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {orders.length === 0 && (
                        <tr><td colSpan={7} className="p-10 text-center text-gray-400 text-sm">No orders yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ 2. PRODUCTS ════ */}
          {currentTab === 'products' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <StatCard label="Total Products" value={adminProducts.length} />
                <StatCard label="Active" value={adminProducts.filter(p => p.status === 'active').length} color="green" />
                <StatCard label="Drafts" value={adminProducts.filter(p => p.status === 'draft').length} />
                <StatCard label="Out of Stock" value={adminProducts.filter(p => p.stock === 0).length} color="red" />
              </div>

              <div className="bg-white p-4 rounded-2xl border border-[#E8E0D8] shadow-sm flex flex-col sm:flex-row gap-3 justify-between sm:items-center">
                <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-200">
                  <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  <input type="text" placeholder="Search products..." className="bg-transparent text-sm outline-none text-gray-600 w-full" />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadAdminProductsData()}
                    className="px-4 py-3 rounded-xl font-bold text-sm border border-[#E8E0D8] text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    title="Refresh products"
                  >
                    {productsLoading
                      ? <svg className="w-4 h-4 animate-spin text-[#F47B20]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                      : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    }
                    Refresh
                  </button>
                  <button onClick={() => setIsProductModalOpen(true)} className="bg-[#F47B20] text-white px-5 py-3 rounded-xl font-bold text-sm hover:bg-[#D96A10] transition-colors flex items-center justify-center gap-2 shadow-sm shadow-orange-200">
                    Add Product
                  </button>
                </div>
              </div>

              {/* Active Products */}
              <div className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                <div className="px-5 py-3 border-b border-[#E8E0D8] flex items-center justify-between">
                  <h3 className="font-bold text-sm text-[#1A1A2E] uppercase tracking-wider">Active Products</h3>
                  <span className="text-xs font-bold text-green-600 bg-green-50 px-2.5 py-1 rounded-full">{adminProducts.filter(p => p.status === 'active').length} live</span>
                </div>
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        <th className="p-4">Bridal Product</th>
                        <th className="p-4">Category</th>
                        <th className="p-4">Price</th>
                        <th className="p-4">Stock</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminProducts.filter(p => p.status !== 'draft').map(p => (
                        <tr key={p.id} className="border-b border-[#E8E0D8] hover:bg-gray-50/50 text-sm transition-colors">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-2xl border border-gray-100 overflow-hidden">
                                {(() => {
                                  const thumb = Array.isArray(p.images)
                                    ? p.images.find(img => !img.match(/\.(mp4|webm|ogg)$/i))
                                    : (p.image && !p.image.match(/\.(mp4|webm|ogg)$/i) ? p.image : null);
                                  return thumb
                                    ? <img src={thumb} alt={p.name} className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setFullscreenImage(thumb)} />
                                    : <span className="text-xl">{p.emoji || '🛍'}</span>;
                                })()}
                              </div>
                              <div>
                                <p className="font-bold text-gray-800">{p.name}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{p.desc}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4"><span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-600">{p.category?.name || p.category || '—'}</span></td>
                          <td className="p-4 font-bold text-gray-800">₦{Number(p.price || 0).toLocaleString()}</td>
                          <td className="p-4 text-gray-600 font-medium">{Number(p.stock || 0)} in stock</td>
                          <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${p.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{p.status}</span></td>
                          <td className="p-4 text-right space-x-2">
                            <button onClick={() => { setEditingProduct({ ...p }); setIsEditProductOpen(true); }} className="text-[#F47B20] hover:text-[#D96A10] transition-colors font-semibold text-xs px-3 py-1.5 border border-[#F47B20]/30 rounded-lg hover:bg-[#FEF0E6]">Edit</button>
                            <button onClick={() => handleDeleteProduct(p.id)} className="text-red-400 hover:text-red-600 transition-colors font-semibold text-xs px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50">Delete</button>
                          </td>
                        </tr>
                      ))}
                      {productsLoading && adminProducts.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-10 text-center">
                            <div className="flex flex-col items-center gap-3 text-gray-400">
                              <svg className="w-7 h-7 animate-spin text-[#F47B20]" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                              </svg>
                              <span className="text-sm">Loading products…</span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {!productsLoading && adminProducts.filter(p => p.status !== 'draft').length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-10 text-center">
                            <div className="flex flex-col items-center gap-3 text-gray-400">
                              <span className="text-sm">No active products found.</span>
                              <button
                                onClick={() => loadAdminProductsData()}
                                className="text-xs font-bold text-[#F47B20] border border-[#F47B20]/30 px-4 py-2 rounded-lg hover:bg-[#FEF0E6] transition-colors"
                              >
                                Retry
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Drafts Section */}
              {adminProducts.filter(p => p.status === 'draft').length > 0 && (
                <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden shadow-sm">
                  <div className="px-5 py-3 border-b border-amber-100 bg-amber-50 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-sm text-amber-800 uppercase tracking-wider">Drafts</h3>
                      <p className="text-[11px] text-amber-600 mt-0.5">These products are hidden from customers. Publish when ready.</p>
                    </div>
                    <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">{adminProducts.filter(p => p.status === 'draft').length} draft{adminProducts.filter(p => p.status === 'draft').length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="overflow-x-auto w-full">
                    <table className="w-full text-left border-collapse min-w-[800px]">
                      <thead>
                        <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                          <th className="p-4">Product</th>
                          <th className="p-4">Category</th>
                          <th className="p-4">Price</th>
                          <th className="p-4">Stock</th>
                          <th className="p-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminProducts.filter(p => p.status === 'draft').map(p => (
                          <tr key={p.id} className="border-b border-[#E8E0D8] bg-amber-50/30 hover:bg-amber-50/60 text-sm transition-colors">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center border border-gray-100 overflow-hidden">
                                  {(() => {
                                    const thumb = Array.isArray(p.images)
                                      ? p.images.find(img => !img.match(/\.(mp4|webm|ogg)$/i))
                                      : (p.image && !p.image.match(/\.(mp4|webm|ogg)$/i) ? p.image : null);
                                    return thumb
                                      ? <img src={thumb} alt={p.name} className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setFullscreenImage(thumb)} />
                                      : <span className="text-xl">{p.emoji || '🛍'}</span>;
                                  })()}
                                </div>
                                <div>
                                  <p className="font-bold text-gray-700">{p.name}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{p.desc}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4"><span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-600">{p.category?.name || p.category || '—'}</span></td>
                            <td className="p-4 font-bold text-gray-700">₦{Number(p.price || 0).toLocaleString()}</td>
                            <td className="p-4 text-gray-500 font-medium">{Number(p.stock || 0)} in stock</td>
                            <td className="p-4 text-right flex items-center justify-end gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    await saveProduct({ ...p, status: 'active' }, p.id);
                                    refreshData();
                                    const refreshed = await fetchAdminProducts();
                                    setAdminProducts(refreshed);
                                    toast.success(`"${p.name}" is now live!`);
                                  } catch(e) { toast.error('Failed to publish product'); }
                                }}
                                className="text-xs font-bold px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                              >
                                Publish
                              </button>
                              <button onClick={() => { setEditingProduct({ ...p }); setIsEditProductOpen(true); }} className="text-[#F47B20] hover:text-[#D96A10] transition-colors font-semibold text-xs px-3 py-1.5 border border-[#F47B20]/30 rounded-lg hover:bg-[#FEF0E6]">Edit</button>
                              <button onClick={() => handleDeleteProduct(p.id)} className="text-red-400 hover:text-red-600 transition-colors font-semibold text-xs px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50">Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════ 3. BANNERS ════ */}
          {currentTab === 'banners' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="font-bold text-base text-gray-800">Promo Banners</h2>
                <button onClick={() => setIsBannerModalOpen(true)} className="bg-[#F47B20] text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-[#D96A10] transition-colors">
                  + New Banner
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {adminBanners.length === 0 && (
                  <p className="col-span-2 text-sm text-gray-400 italic py-4">No banners yet. Click "+ New Banner" to add one.</p>
                )}
                {adminBanners.map(b => (
                  <div key={b.id} className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                    {/* Banner Image — fills a fixed-height container */}
                    <div className="relative w-full h-44 overflow-hidden bg-gray-100">
                      {b.image ? (
                        <img src={b.image} alt={b.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F47B20] to-[#E05C00] text-white text-4xl"></div>
                      )}
                      {/* Overlay text */}
                      <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-4">
                        <h3 className="text-white font-black text-lg leading-tight drop-shadow">{b.title}</h3>
                        <p className="text-white/85 text-sm mt-0.5 drop-shadow">{b.subtitle}</p>
                      </div>
                      <span className={`absolute top-3 right-3 text-[10px] font-black uppercase px-2 py-1 rounded-full ${b.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{b.status}</span>
                    </div>
                    <div className="p-4 flex justify-between items-center text-sm border-t border-gray-100">
                      <span className="text-gray-400 text-xs">{b.start} – {b.end}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEditingBanner({ ...b }); setIsEditBannerOpen(true); }}
                          className="text-[#F47B20] font-bold text-xs px-3 py-1.5 border border-[#F47B20]/30 rounded-lg hover:bg-[#FEF0E6]"
                        >
                          Edit
                        </button>
                        <button onClick={() => handleDeleteBanner(b.id)} className="text-red-500 hover:text-red-700 font-bold text-xs px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ════ 3a. CATEGORIES ════ */}
          {currentTab === 'categories' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="font-bold text-base text-gray-800">Categories</h2>
                <button onClick={() => setIsCategoryModalOpen(true)} className="bg-[#F47B20] text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-[#D96A10] transition-colors">
                  + New Category
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(!categories || categories.length === 0) && (
                  <p className="col-span-4 text-sm text-gray-400 italic py-4">No categories yet. Click "+ New Category" to add one.</p>
                )}
                {(categories || []).map(cat => (
                  <div key={cat.id} className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm flex flex-col">
                    <div className="relative w-full aspect-square overflow-hidden bg-gray-100">
                      {cat.image_url ? (
                        <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#FEF0E6] text-[#F47B20] text-sm font-medium">No Image</div>
                      )}
                    </div>
                    <div className="p-3 text-center border-b border-gray-100 flex-1 flex items-center justify-center">
                      <h3 className="text-gray-800 font-bold text-sm leading-tight">{cat.name}</h3>
                    </div>
                    <div className="p-2 flex justify-between items-center bg-gray-50/50">
                      <button
                        onClick={() => { setEditingCategory({ ...cat, imageUrl: cat.image_url }); setIsEditCategoryOpen(true); }}
                        className="text-[#F47B20] font-bold text-xs px-3 py-1.5 hover:bg-[#FEF0E6] rounded-lg transition-colors flex-1 text-center"
                      >
                        Edit
                      </button>
                      <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:text-red-700 font-bold text-xs px-3 py-1.5 hover:bg-red-50 rounded-lg transition-colors flex-1 text-center">
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ════ 3b. HERO CAROUSEL ════ */}
          {currentTab === 'carousel' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="font-bold text-base text-gray-800">Hero Carousel Slides</h2>
                <button onClick={() => setIsSlideModalOpen(true)} className="bg-[#F47B20] text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-[#D96A10] transition-colors">
                  + Add Slide
                </button>
              </div>
              <p className="text-xs text-gray-400 -mt-4">Changes here instantly update the homepage carousel.</p>

              <div className="space-y-4">
                {adminHeroSlides.length === 0 && (
                  <p className="text-sm text-gray-400 italic py-4">No slides yet. Click "+ Add Slide" to add one.</p>
                )}
                {adminHeroSlides.map((s, idx) => (
                  <div key={s.id} className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                    <div className="relative h-36 overflow-hidden">
                      {s.image ? (
                        <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#F47B20] to-[#1A1A2E]" />
                      )}
                      <div className="absolute inset-0 bg-black/45 flex flex-col justify-center px-6">
                        <p className="text-white font-black text-lg drop-shadow">{s.title}</p>
                        {s.subtitle && <p className="text-white/80 text-sm mt-0.5 drop-shadow">{s.subtitle}</p>}
                      </div>
                      <span className="absolute top-3 left-3 text-[10px] font-black uppercase px-2 py-1 rounded-full bg-black/60 text-white">
                        Slide {idx + 1}
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center border-t border-gray-100">
                      <div className="text-xs text-gray-400 space-x-3">
                        <span>CTA: <strong className="text-gray-700">{s.cta || 'Shop Now'}</strong></span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setEditingSlide({ ...s }); setIsEditSlideOpen(true); }}
                          className="text-[#F47B20] font-bold text-xs px-3 py-1.5 border border-[#F47B20]/30 rounded-lg hover:bg-[#FEF0E6]"
                        >
                          Edit
                        </button>
                        <button onClick={() => handleDeleteSlide(s.id)} className="text-red-500 font-bold text-xs px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

              </div>
            </div>
          )}

          {/* ════ 4. ORDERS ════ */}
          {currentTab === 'orders' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                {/* Header + search */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-[#E8E0D8]">
                  <div>
                    <h2 className="font-bold text-sm text-[#1A1A2E] uppercase tracking-wider">All Orders</h2>
                    <span className="text-[11px] text-gray-400 mt-0.5 block">Click any row to view &amp; update</span>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-200 w-full sm:w-72">
                    <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input
                      type="text"
                      placeholder="Search by customer, order ID or product..."
                      value={dashboardOrderSearch}
                      onChange={e => setDashboardOrderSearch(e.target.value)}
                      className="bg-transparent text-sm outline-none text-gray-600 w-full"
                    />
                    {dashboardOrderSearch && (
                      <button onClick={() => setDashboardOrderSearch('')} className="text-gray-300 hover:text-gray-500">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    )}
                  </div>
                </div>
                {/* Status filter tabs */}
                <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[#E8E0D8] overflow-x-auto">
                  {[
                    { key: 'all',        label: 'All',        count: orders.length },
                    { key: 'pending',    label: 'Pending',    count: orders.filter(o => o.status === 'pending').length },
                    { key: 'processing', label: 'Processing', count: orders.filter(o => o.status === 'processing').length },
                    { key: 'shipped',    label: 'Shipped',    count: orders.filter(o => o.status === 'shipped').length },
                    { key: 'delivered',  label: 'Delivered',  count: orders.filter(o => o.status === 'delivered').length },
                    { key: 'cancelled',  label: 'Cancelled',  count: orders.filter(o => o.status === 'cancelled').length },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setDashboardStatusFilter(f.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                        dashboardStatusFilter === f.key ? 'bg-[#F47B20] text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {f.label}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${
                        dashboardStatusFilter === f.key ? 'bg-white/30 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>{f.count}</span>
                    </button>
                  ))}
                </div>
                {/* Table */}
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        <th className="p-4">Order ID</th>
                        <th className="p-4">Customer</th>
                        <th className="p-4">Product</th>
                        {currentRole !== 'staff' && <th className="p-4">Amount</th>}
                        <th className="p-4">Date</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = orders.filter(o => {
                          const matchesStatus = dashboardStatusFilter === 'all' || o.status === dashboardStatusFilter;
                          if (!matchesStatus) return false;
                          if (!dashboardOrderSearch) return true;
                          const q = dashboardOrderSearch.toLowerCase();
                          return (
                            String(o.id).toLowerCase().includes(q) ||
                            (o.customer || '').toLowerCase().includes(q) ||
                            (o.product || '').toLowerCase().includes(q)
                          );
                        });
                        if (filtered.length === 0) return (
                          <tr><td colSpan={7} className="p-10 text-center text-gray-400 text-sm">No orders match your filter.</td></tr>
                        );
                        return filtered.map(o => {
                          const isCompleted = o.status === 'delivered' || o.status === 'cancelled';
                          return (
                            <tr
                              key={o.id}
                              onClick={() => { setSelectedOrder({ ...o }); setIsOrderModalOpen(true); }}
                              className={`border-b border-[#E8E0D8] text-sm cursor-pointer transition-colors ${isCompleted ? 'opacity-50 bg-gray-50/60 hover:bg-gray-100/60' : 'hover:bg-[#FEF0E6]/40'}`}
                            >
                              <td className={`p-4 font-bold ${isCompleted ? 'text-gray-400' : 'text-[#F47B20]'}`}>{o.id}</td>
                              <td className="p-4 text-gray-700 font-medium">{o.customer}</td>
                              <td className="p-4 text-gray-600">{o.product}</td>
                              {currentRole !== 'staff' && <td className="p-4 font-bold text-gray-800">{o.amount}</td>}
                              <td className="p-4 text-gray-400 text-xs whitespace-nowrap">{o.date}</td>
                              <td className="p-4">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${ORDER_STATUS_STYLES[o.status] || 'bg-gray-100 text-gray-500'}`}>
                                  {o.status}
                                </span>
                              </td>
                              <td className="p-4 text-right" onClick={e => e.stopPropagation()}>
                                <button
                                  onClick={() => { setSelectedOrder({ ...o }); setIsOrderModalOpen(true); }}
                                  className="text-xs font-bold px-4 py-2 bg-orange-50 border border-orange-200 text-orange-600 rounded-xl hover:bg-orange-100 hover:border-orange-300 transition-colors"
                                >
                                  View / Update
                                </button>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ 4b. CUSTOMERS ════ */}
          {currentTab === 'customers' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-3">
                <h2 className="font-bold text-base text-gray-800">Customers ({customers.length})</h2>
                <input
                  type="text"
                  placeholder="Search customers..."
                  className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20] w-full md:w-64"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                />
              </div>
              <div className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        <th className="p-4">Name</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Phone</th>
                        <th className="p-4">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase()) || c.email?.toLowerCase().includes(customerSearch.toLowerCase())).map(c => (
                        <tr key={c.id} className="border-b border-[#E8E0D8] hover:bg-gray-50 text-sm transition-colors">
                          <td className="p-4 font-bold text-gray-800">{c.name}</td>
                          <td className="p-4 text-gray-600">{c.email}</td>
                          <td className="p-4 text-gray-600">{c.phone || '—'}</td>
                          <td className="p-4 text-gray-400 text-xs">
                            {new Date(c.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </td>
                        </tr>
                      ))}
                      {customers.length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-400 italic text-sm">No customers found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ 5. CHATS ════ */}
          {currentTab === 'chats' && (
            <div className="bg-white rounded-3xl border border-[#E8E0D8] overflow-hidden shadow-sm flex flex-col md:flex-row h-[520px]">
              {/* Thread list */}
              <div className={`w-full md:w-80 border-r border-[#E8E0D8] bg-gray-50/50 flex-col h-full ${selectedThreadId ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-[#E8E0D8]">
                  <h3 className="font-bold text-sm text-[#1A1A2E]">Customer Inboxes</h3>
                </div>
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                  {chatThreads.map(thread => {
                    const isActive = thread.id === selectedThreadId;
                    return (
                      <button
                        key={thread.id}
                        onClick={() => setSelectedThreadId(thread.id)}
                        className={`w-full text-left p-4 transition-colors flex flex-col gap-1 ${isActive ? 'bg-[#FEF0E6] text-[#F47B20]' : 'hover:bg-gray-50 text-gray-700'}`}
                      >
                        <div className="flex justify-between items-center w-full">
                          <span className="font-bold text-xs md:text-sm">{thread.customerName}</span>
                          <span className="text-[10px] text-gray-400">{thread.time}</span>
                        </div>
                        <p className={`text-xs truncate w-full ${isActive ? 'text-[#F47B20]/80' : 'text-gray-400'}`}>{thread.lastMessage}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message pane */}
              {(() => {
                const activeThread = chatThreads.find(t => t.id === selectedThreadId);
                return (
                  <div className={`flex-1 flex-col h-full bg-[#FAF9F6] ${selectedThreadId ? 'flex' : 'hidden md:flex'}`}>
                    {activeThread ? (
                      <>
                        <div className="p-4 border-b border-[#E8E0D8] bg-white flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <button onClick={() => setSelectedThreadId(null)} className="md:hidden text-gray-500 hover:text-gray-800 p-1 mr-1">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <span className="font-bold text-sm text-[#1A1A2E]">{activeThread.customerName}</span>
                          </div>
                          <span className="text-[10px] text-green-500 font-bold">● Connected</span>
                        </div>
                        <div className="flex-1 p-4 overflow-y-auto space-y-4">
                          {activeThreadMessages.map((msg, idx) => {
                            const isStaff = msg.is_admin_reply;
                            const timeStr = msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                            return (
                              <div key={idx} className={`flex flex-col ${isStaff ? 'items-end' : 'items-start'}`}>
                                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-xs shadow-sm leading-relaxed whitespace-pre-wrap ${isStaff ? 'bg-[#F47B20] text-white rounded-tr-none' : 'bg-white text-[#1A1A2E] rounded-tl-none border border-gray-100'}`}>
                                  {(() => {
                                    const text = msg.message || '';
                                    if (text.includes('Attached Image: http')) {
                                      const [main, imgUrl] = text.split('Attached Image: ');
                                      return (
                                        <>
                                          {main}
                                          <div className="mt-2">
                                            <a href={imgUrl} target="_blank" rel="noreferrer">
                                              <img src={imgUrl} alt="Attached" className="max-w-[150px] rounded-lg border border-gray-200" />
                                            </a>
                                          </div>
                                        </>
                                      );
                                    }
                                    return text;
                                  })()}
                                </div>
                                <span className="text-[9px] text-gray-400 mt-1 px-1">{timeStr}</span>
                              </div>
                            );
                          })}
                          <div ref={chatEndRef} />
                        </div>
                        <div className="p-3 bg-white border-t border-[#E8E0D8] flex flex-col gap-2">
                          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                            {['Order number please?', 'You have pending unpaid items', 'How can I assist you today?'].map((suggestion, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => setAdminChatInput(suggestion)}
                                className="whitespace-nowrap bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-[10px] transition-colors"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                          <form onSubmit={handleSendAdminMessage} className="flex gap-2">
                            <textarea
                              rows="2"
                              placeholder="Type a reply to customer..."
                              value={adminChatInput}
                              onChange={(e) => setAdminChatInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSendAdminMessage(e);
                                }
                              }}
                              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-xs outline-none focus:border-[#F47B20] resize-none"
                            />
                            <button type="submit" className="bg-[#1A1A2E] hover:bg-black text-white px-4 py-2.5 rounded-xl text-xs font-bold transition-colors">Reply</button>
                          </form>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-gray-400 text-xs italic">Select a thread to view messages</div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ════ 9. SHIPPING SETTINGS ════ */}
          {currentTab === 'shipping' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div>
                  <h2 className="text-xl font-bold text-[#1A1A2E]">Shipping Configuration</h2>
                  <p className="text-sm text-gray-500 mt-1">Manage global shipping rates and toggle free shipping on or off.</p>
                </div>
                <button
                  onClick={async () => {
                    setShippingSaving(true);
                    try {
                      await updateShippingSettings(shippingConfig);
                      toast.success('Shipping settings saved!');
                    } catch (err) {
                      toast.error('Failed to save shipping settings');
                    }
                    setShippingSaving(false);
                  }}
                  disabled={shippingSaving}
                  className="w-full sm:w-auto bg-[#1A1A2E] hover:bg-black text-white px-6 py-2.5 rounded-xl font-bold transition-colors disabled:bg-gray-300"
                >
                  {shippingSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="max-w-xl space-y-6">
                  {/* Toggle */}
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <label className="font-bold text-[#1A1A2E] block">Enable Global Shipping</label>
                      <span className="text-xs text-gray-500">If turned off, all orders will receive Free Shipping.</span>
                    </div>
                    <button
                      onClick={() => setShippingConfig(p => ({ ...p, shipping_enabled: !p.shipping_enabled }))}
                      className={`shrink-0 relative w-12 h-6 rounded-full transition-colors ${shippingConfig.shipping_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${shippingConfig.shipping_enabled ? 'translate-x-6' : ''}`} />
                    </button>
                  </div>

                  {shippingConfig.shipping_enabled && (
                    <>
                      <div>
                        <label className="block text-sm font-bold text-[#1A1A2E] mb-2">Lagos & Kwara Shipping Fee (₦)</label>
                        <input
                          type="number"
                          value={shippingConfig.shipping_fee_lagos_kwara}
                          onChange={(e) => setShippingConfig(p => ({ ...p, shipping_fee_lagos_kwara: Number(e.target.value) }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-[#F47B20] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#1A1A2E] mb-2">Other States Shipping Fee (₦)</label>
                        <input
                          type="number"
                          value={shippingConfig.shipping_fee_other}
                          onChange={(e) => setShippingConfig(p => ({ ...p, shipping_fee_other: Number(e.target.value) }))}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-[#F47B20] transition-colors"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ════ 7. ADMIN USERS ════ */}
          {currentTab === 'users' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="font-bold text-base text-gray-800">Staff Members</h2>
                <button onClick={() => setIsUserModalOpen(true)} className="bg-[#F47B20] text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-[#D96A10] transition-colors">
                  Add Staff Member
                </button>
              </div>
              <div className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                <div className="overflow-x-auto w-full">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        <th className="p-4">Name</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Role</th>
                        <th className="p-4">Last Active</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users
                        .filter(u => !(currentUser?.role !== 'root' && u.role === 'root'))
                        .filter(u => u.id !== currentUser?.id)
                        .map(u => (
                        <tr key={u.id} className="border-b border-[#E8E0D8] hover:bg-gray-50/50 text-sm">
                          <td className="p-4 font-bold text-gray-800">{u.name}</td>
                          <td className="p-4 text-gray-600">{u.email}</td>
                          <td className="p-4">
                            <span className="text-xs font-bold px-2.5 py-1 bg-[#FEF0E6] text-[#F47B20] rounded-full uppercase">{u.role}</span>
                          </td>
                          <td className="p-4 text-gray-400">{u.lastActive}</td>
                          <td className="p-4">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${u.disabled ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'}`}>
                              {u.disabled ? 'Disabled' : 'Active'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => { setEditingUser({ ...u }); setIsEditUserOpen(true); }}
                                title="Edit"
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#F47B20]/30 text-[#F47B20] hover:bg-[#FEF0E6] transition-colors"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </button>
                              <button
                                onClick={() => toggleUserStatus(u.id)}
                                title={u.disabled ? 'Enable' : 'Disable'}
                                className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${u.disabled ? 'border-green-200 text-green-600 hover:bg-green-50' : 'border-amber-200 text-amber-600 hover:bg-amber-50'}`}
                              >
                                {u.disabled
                                  ? <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                  : <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                                }
                              </button>
                              {u.role !== 'root' && (
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                title="Delete"
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ INCOME LOG ════ */}
          {currentTab === 'income' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-3xl border border-[#E8E0D8] shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="font-black text-xl text-[#1A1A2E]">Income Log Sheet</h2>
                    <p className="text-xs text-gray-400 mt-0.5">All paid &amp; completed orders with customer details and timestamps</p>
                  </div>
                  <button
                    onClick={() => {
                      const iframe = document.createElement('iframe');
                      iframe.style.display = 'none';
                      document.body.appendChild(iframe);
                      const rows = orders
                        .filter(o => (o.paymentStatus === 'paid' || o.status === 'delivered') && o.paymentStatus !== 'refunded' && o.paymentStatus !== 'refund_pending')
                        .map((o, i) => {
                          const dateStr = (o.created_at || o.date) 
                            ? new Date(o.created_at || o.date).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })
                            : '—';
                          return `
                            <tr>
                              <td>${i + 1}</td>
                              <td>${o.id || '—'}</td>
                              <td>${o.customer || o.name || '—'}</td>
                              <td>${o.email || '—'}</td>
                              <td>${o.product || o.item || '—'}</td>
                              <td>&#8358;${Number(o.price || 0).toLocaleString()}</td>
                              <td>${o.status || '—'}</td>
                              <td>${dateStr}</td>
                            </tr>
                          `;
                        }).join('');
                      const doc = iframe.contentWindow.document;
                      doc.open();
                      doc.write(`
                        <html>
                          <head>
                            <title>Manna Bridal - Income Log</title>
                            <style>
                              body { font-family: sans-serif; padding: 40px; color: #1A1A2E; }
                              h1 { color: #F47B20; border-bottom: 2px solid #FEF0E6; padding-bottom: 10px; }
                              table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
                              th { background: #FEF0E6; color: #F47B20; padding: 10px; text-align: left; font-weight: bold; text-transform: uppercase; letter-spacing: 0.05em; }
                              td { padding: 10px; border-bottom: 1px solid #f3f4f6; }
                              tr:nth-child(even) td { background: #fafafa; }
                            </style>
                          </head>
                          <body>
                            <h1>Manna Bridal - Income Log Sheet</h1>
                            <p>Generated: ${new Date().toLocaleString()}</p>
                            <table>
                              <thead><tr><th>#</th><th>Order ID</th><th>Customer</th><th>Email</th><th>Product</th><th>Amount</th><th>Status</th><th>Date &amp; Time</th></tr></thead>
                              <tbody>${rows}</tbody>
                            </table>
                            <script>
                              window.onload = () => { setTimeout(() => { window.print(); }, 500); };
                            </script>
                          </body>
                        </html>
                      `);
                      doc.close();
                      // Clean up iframe after print dialog closes (approx)
                      setTimeout(() => { document.body.removeChild(iframe); }, 10000);
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#F47B20] text-white rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                    Export PDF
                  </button>
                </div>

                {/* Summary row */}
                {(() => {
                  const paidOrders = orders.filter(o => (o.paymentStatus === 'paid' || o.status === 'delivered') && o.paymentStatus !== 'refunded' && o.paymentStatus !== 'refund_pending');
                  const total = paidOrders.reduce((s, o) => s + Number(o.price || 0), 0);
                  const now = new Date();
                  const todayTotal = paidOrders.filter(o => {
                    const d = new Date(o.created_at || o.date);
                    return !isNaN(d.getTime()) && (now - d) / (1000 * 60 * 60 * 24) <= 1;
                  }).reduce((s, o) => s + Number(o.price || 0), 0);
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                      {[
                        { label: 'Total Paid Orders', value: paidOrders.length },
                        { label: 'All-Time Revenue', value: `₦${total.toLocaleString()}` },
                        { label: 'Revenue Today', value: `₦${todayTotal.toLocaleString()}` },
                        { label: 'Avg. Order Value', value: paidOrders.length ? `₦${Math.round(total / paidOrders.length).toLocaleString()}` : '₦0' },
                      ].map(s => (
                        <div key={s.label} className="bg-[#FAF8F5] rounded-2xl p-4 border border-[#E8E0D8]">
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{s.label}</div>
                          <div className="text-lg font-black text-[#1A1A2E]">{s.value}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Log Table */}
                <div className="overflow-x-auto rounded-2xl border border-[#E8E0D8]">
                  <table className="w-full text-left border-collapse min-w-[720px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        <th className="p-4">#</th>
                        <th className="p-4">Order ID</th>
                        <th className="p-4">Customer</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Product</th>
                        <th className="p-4">Amount</th>
                        <th className="p-4">Status</th>
                        <th className="p-4">Date &amp; Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders
                        .filter(o => (o.paymentStatus === 'paid' || o.status === 'delivered') && o.paymentStatus !== 'refunded' && o.paymentStatus !== 'refund_pending')
                        .sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0))
                        .map((o, idx) => {
                          const dateStr = (o.created_at || o.date)
                            ? new Date(o.created_at || o.date).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })
                            : '—';
                          return (
                            <tr key={o.id || idx} className="border-b border-gray-100 hover:bg-[#FEF8F3] transition-colors">
                              <td className="p-4 text-gray-400 text-xs">{idx + 1}</td>
                              <td className="p-4 font-mono text-xs text-gray-600">#{o.id}</td>
                              <td className="p-4 font-semibold text-sm text-[#1A1A2E]">{o.customer || o.name || '—'}</td>
                              <td className="p-4 text-xs text-gray-500">{o.email || '—'}</td>
                              <td className="p-4 text-sm text-gray-700 max-w-[180px] truncate">{o.product || o.item || '—'}</td>
                              <td className="p-4 font-black text-[#F47B20] text-sm">₦{Number(o.price || 0).toLocaleString()}</td>
                              <td className="p-4">
                                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase ${ORDER_STATUS_STYLES[o.status] || 'bg-gray-100 text-gray-500'}`}>
                                  {o.status}
                                </span>
                              </td>
                              <td className="p-4 text-xs text-gray-500 whitespace-nowrap">{dateStr}</td>
                            </tr>
                          );
                        })
                      }
                      {orders.filter(o => (o.paymentStatus === 'paid' || o.status === 'delivered') && o.paymentStatus !== 'refunded' && o.paymentStatus !== 'refund_pending').length === 0 && (
                        <tr>
                          <td colSpan="8" className="p-8 text-center text-gray-400">No income records found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ 6b. REFUNDS LOG ════ */}
          {currentTab === 'refunds' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-3xl border border-[#E8E0D8] shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="font-black text-xl text-[#1A1A2E]">Refunds Log Sheet</h2>
                    <p className="text-xs text-gray-400 mt-0.5">All refunded or pending refund orders</p>
                  </div>
                  <button
                    onClick={() => {
                      const iframe = document.createElement('iframe');
                      iframe.style.display = 'none';
                      document.body.appendChild(iframe);
                      const rows = orders
                        .filter(o => o.paymentStatus === 'refunded' || o.paymentStatus === 'refund_pending')
                        .map((o, i) => {
                          const dateStr = (o.created_at || o.date) 
                            ? new Date(o.created_at || o.date).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })
                            : '—';
                          return `
                            <tr>
                              <td>${i + 1}</td>
                              <td>${o.id || '—'}</td>
                              <td>${o.customer || o.name || '—'}</td>
                              <td>${o.email || '—'}</td>
                              <td>${o.product || o.item || '—'}</td>
                              <td>&#8358;${Number(o.price || 0).toLocaleString()}</td>
                              <td>${o.paymentStatus || '—'}</td>
                              <td>${dateStr}</td>
                            </tr>
                          `;
                        }).join('');
                      const doc = iframe.contentWindow.document;
                      doc.open();
                      doc.write(`
                        <html>
                          <head>
                            <title>Manna Bridal - Refunds Log</title>
                            <style>
                              body { font-family: sans-serif; padding: 40px; color: #1A1A2E; }
                              h1 { color: #e11d48; border-bottom: 2px solid #ffe4e6; padding-bottom: 10px; }
                              table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
                              th { background: #ffe4e6; color: #e11d48; padding: 10px; text-align: left; font-weight: bold; text-transform: uppercase; letter-spacing: 0.05em; }
                              td { padding: 10px; border-bottom: 1px solid #f3f4f6; }
                              tr:nth-child(even) td { background: #fafafa; }
                            </style>
                          </head>
                          <body>
                            <h1>Manna Bridal - Refunds Log Sheet</h1>
                            <p>Generated: ${new Date().toLocaleString()}</p>
                            <table>
                              <thead><tr><th>#</th><th>Order ID</th><th>Customer</th><th>Email</th><th>Product</th><th>Amount</th><th>Status</th><th>Date &amp; Time</th></tr></thead>
                              <tbody>${rows}</tbody>
                            </table>
                            <script>
                              window.onload = () => { setTimeout(() => { window.print(); }, 500); };
                            </script>
                          </body>
                        </html>
                      `);
                      doc.close();
                      setTimeout(() => { document.body.removeChild(iframe); }, 10000);
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-bold hover:bg-rose-600 transition-colors shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                    Export PDF
                  </button>
                </div>

                {/* Summary row */}
                {(() => {
                  const refundedOrders = orders.filter(o => o.paymentStatus === 'refunded' || o.paymentStatus === 'refund_pending');
                  const total = refundedOrders.reduce((s, o) => s + Number(o.price || 0), 0);
                  const now = new Date();
                  const todayTotal = refundedOrders.filter(o => {
                    const d = new Date(o.created_at || o.date);
                    return !isNaN(d.getTime()) && (now - d) / (1000 * 60 * 60 * 24) <= 1;
                  }).reduce((s, o) => s + Number(o.price || 0), 0);
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                      {[
                        { label: 'Total Refunded Orders', value: refundedOrders.length },
                        { label: 'All-Time Refunds', value: `₦${total.toLocaleString()}` },
                        { label: 'Refunds Today', value: `₦${todayTotal.toLocaleString()}` },
                        { label: 'Avg. Refund Value', value: refundedOrders.length ? `₦${Math.round(total / refundedOrders.length).toLocaleString()}` : '₦0' },
                      ].map(s => (
                        <div key={s.label} className="bg-rose-50 rounded-2xl p-4 border border-rose-100">
                          <div className="text-[10px] font-bold text-rose-400 uppercase tracking-wider mb-1">{s.label}</div>
                          <div className="text-lg font-black text-rose-900">{s.value}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Log Table */}
                <div className="overflow-x-auto rounded-2xl border border-rose-100">
                  <table className="w-full text-left border-collapse min-w-[720px]">
                    <thead>
                      <tr className="bg-rose-50 border-b border-rose-100 text-[10px] uppercase font-bold text-rose-400 tracking-wider">
                        <th className="p-4">#</th>
                        <th className="p-4">Order ID</th>
                        <th className="p-4">Customer</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Product</th>
                        <th className="p-4">Amount</th>
                        <th className="p-4">Status</th>
                        <th className="p-4">Date &amp; Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders
                        .filter(o => o.paymentStatus === 'refunded' || o.paymentStatus === 'refund_pending')
                        .sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0))
                        .map((o, idx) => {
                          const dateStr = (o.created_at || o.date)
                            ? new Date(o.created_at || o.date).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })
                            : '—';
                          return (
                            <tr key={o.id || idx} className="border-b border-rose-50 hover:bg-rose-50 transition-colors">
                              <td className="p-4 text-rose-400 text-xs">{idx + 1}</td>
                              <td className="p-4 font-mono text-xs text-rose-600">#{o.id}</td>
                              <td className="p-4 font-semibold text-sm text-rose-900">{o.customer || o.name || '—'}</td>
                              <td className="p-4 text-xs text-rose-500">{o.email || '—'}</td>
                              <td className="p-4 text-sm text-rose-700 max-w-[180px] truncate">{o.product || o.item || '—'}</td>
                              <td className="p-4 font-black text-rose-600 text-sm">₦{Number(o.price || 0).toLocaleString()}</td>
                              <td className="p-4">
                                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase ${o.paymentStatus === 'refunded' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                                  {o.paymentStatus}
                                </span>
                              </td>
                              <td className="p-4 text-xs text-rose-500 whitespace-nowrap">{dateStr}</td>
                            </tr>
                          );
                        })
                      }
                      {orders.filter(o => o.paymentStatus === 'refunded' || o.paymentStatus === 'refund_pending').length === 0 && (
                        <tr>
                          <td colSpan="8" className="p-8 text-center text-rose-400">No refund records found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ════ 8. ROOT CMS ════ */}
          {currentTab === 'root_cms' && isRoot && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <div>
                  <h2 className="font-black text-xl text-[#1A1A2E]">Root Super-Admin CMS</h2>
                  <p className="text-xs text-gray-400">Full system control — use with care.</p>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'roles', label: 'Role Manager' },
                  { id: 'payment', label: 'Payment Settings' },
                  { id: 'logs', label: 'System Logs' },
                  { id: 'purge', label: 'Data Purge' },
                  { id: 'backup', label: 'Backup & Restore' },
                  { id: 'broadcast', label: 'Broadcast' },
                  { id: 'report', label: 'Reports' },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setRootCmsTab(t.id)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                      rootCmsTab === t.id
                        ? 'bg-[#1A1A2E] text-white shadow'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-[#F47B20] hover:text-[#F47B20]'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {rootNotice && (
                <div className={`px-4 py-3 rounded-xl text-sm font-semibold ${
                  rootNotice.startsWith('Error') ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
                }`}>
                  {rootNotice}
                </div>
              )}

              {/* Role Manager */}
              {rootCmsTab === 'roles' && (
                <div className="bg-white rounded-2xl border border-[#E8E0D8] overflow-hidden shadow-sm">
                  <SectionHeader title="All System Users — Assign Roles" />
                  <div className="overflow-x-auto">
                    <table className="w-full text-left min-w-[600px]">
                      <thead>
                        <tr className="bg-gray-50 border-b border-[#E8E0D8] text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                          <th className="p-4">Name</th>
                          <th className="p-4">Email</th>
                          <th className="p-4">Current Role</th>
                          <th className="p-4">Change Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rootUsers.map(u => (
                          <tr key={u.id} className="border-b border-[#E8E0D8] text-sm hover:bg-gray-50">
                            <td className="p-4 font-bold text-gray-800">{u.name}</td>
                            <td className="p-4 text-gray-500">{u.email}</td>
                            <td className="p-4">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-black uppercase ${
                                u.role === 'root' ? 'bg-purple-100 text-purple-700'
                                : u.role === 'admin' ? 'bg-orange-100 text-orange-600'
                                : u.role === 'staff' ? 'bg-blue-100 text-blue-600'
                                : 'bg-gray-100 text-gray-600'
                              }`}>{u.role}</span>
                            </td>
                            <td className="p-4">
                              <select
                                defaultValue={u.role}
                                disabled={u.role && u.role !== 'customer' && u.role !== ''}
                                onChange={async (e) => {
                                  await doRootAction(
                                    () => rootAssignRole(u.id, e.target.value),
                                    `Role updated for ${u.name}`
                                  );
                                  setRootUsers(prev => prev.map(x => x.id === u.id ? { ...x, role: e.target.value } : x));
                                }}
                                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white outline-none focus:border-[#F47B20]"
                              >
                                <option value="customer">Customer</option>
                                <option value="staff">Staff</option>
                                <option value="admin">Admin</option>
                                <option value="root">Root</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment Settings */}
              {rootCmsTab === 'payment' && (
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                  <h3 className="font-bold text-sm text-gray-800">Paystack Integration Keys</h3>
                  
                  {paymentAuthStep === 0 && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">For your security, please enter your password to view or modify Payment Settings.</p>
                      <input
                        type="password"
                        placeholder="Your Password"
                        className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]"
                        value={paymentAuthPassword}
                        onChange={e => setPaymentAuthPassword(e.target.value)}
                      />
                      <button
                        disabled={rootLoading || !paymentAuthPassword}
                        onClick={async () => {
                          try {
                            setRootLoading(true);
                            await rootRequestPaymentAuth(paymentAuthPassword);
                            setPaymentAuthStep(1);
                            toast.success('OTP sent to your email.');
                          } catch (e) {
                            toast.error(e?.response?.data?.message || 'Authentication failed');
                          } finally {
                            setRootLoading(false);
                          }
                        }}
                        className="bg-[#F47B20] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-50"
                      >
                        {rootLoading ? 'Verifying...' : 'Continue'}
                      </button>
                    </div>
                  )}

                  {paymentAuthStep === 1 && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">An OTP has been sent to your email. Please enter it below.</p>
                      <input
                        type="text"
                        placeholder="6-digit OTP"
                        maxLength={6}
                        className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]"
                        value={paymentAuthOtp}
                        onChange={e => setPaymentAuthOtp(e.target.value.replace(/\D/g, ''))}
                      />
                      <button
                        disabled={rootLoading || paymentAuthOtp.length !== 6}
                        onClick={async () => {
                          try {
                            setRootLoading(true);
                            const res = await rootVerifyPaymentAuth(paymentAuthOtp);
                            setPaymentAuthToken(res.token);
                            setPaymentAuthStep(2);
                            // Fetch config now that we have the token
                            const config = await rootGetPaymentConfig(res.token);
                            setRootPayConfig(config);
                            toast.success('Settings unlocked!');
                          } catch (e) {
                            toast.error(e?.response?.data?.message || 'OTP verification failed');
                          } finally {
                            setRootLoading(false);
                          }
                        }}
                        className="bg-[#F47B20] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-50"
                      >
                        {rootLoading ? 'Verifying OTP...' : 'Unlock Settings'}
                      </button>
                    </div>
                  )}

                  {paymentAuthStep === 2 && (
                    <>
                      <p className="text-xs text-gray-500 -mt-2">Configure your payment gateway. Get these keys from your Paystack Dashboard.</p>
                      {['paystack_public_key', 'paystack_secret_key'].map(field => (
                        <div key={field} className="space-y-1">
                          <label className="text-[10px] font-bold text-gray-400 uppercase">{field.replace(/_/g, ' ')}</label>
                          <input
                            className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20] font-mono"
                            value={rootPayConfig[field] || ''}
                            onChange={e => setRootPayConfig(prev => ({ ...prev, [field]: e.target.value }))}
                            placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                          />
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <button
                          disabled={rootLoading}
                          onClick={async () => {
                            try {
                              await doRootAction(() => rootUpdatePaymentConfig(rootPayConfig, paymentAuthToken), 'Payment config saved!');
                              toast.success('Payment config saved');
                            } catch (e) {
                              toast.error('Failed to save config');
                            }
                          }}
                          className="bg-[#F47B20] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-50"
                        >
                          {rootLoading ? 'Saving...' : 'Save Payment Config'}
                        </button>
                        <button
                          disabled={rootLoading}
                          onClick={async () => {
                            try {
                              const res = await rootReverifyPayments();
                              toast.success(res.message || 'Re-verification complete!');
                            } catch (e) {
                              toast.error('Re-verification failed.');
                            }
                          }}
                          className="ml-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          🔄 Re-verify Unpaid Orders
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Finance Dashboard */}
              {rootCmsTab === 'finance' && (() => {
                const today = new Date().toISOString().slice(0,10);
                const thisMonth = new Date().toISOString().slice(0,7);
                const thisYear = new Date().toISOString().slice(0,4);

                const filterOptions = [
                  { id: 'all', label: 'All Time' },
                  { id: 'year', label: 'This Year' },
                  { id: 'month', label: 'This Month' },
                  { id: 'today', label: 'Today' },
                ];

                return (
                  <div className="bg-white rounded-2xl border border-[#E8E0D8] p-6 shadow-sm space-y-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="font-bold text-sm text-gray-800">Finance Dashboard</h3>
                      <div className="flex flex-wrap gap-2">
                        {filterOptions.map(opt => (
                          <button
                            key={opt.id}
                            onClick={() => setRootFinanceFilter(opt.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                              rootFinanceFilter === opt.id
                                ? 'bg-[#F47B20] text-white shadow'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {rootFinance ? (() => {
                      const ds = rootFinance.daily_sales || {};
                      const ms = rootFinance.monthly_sales || {};
                      const ys = rootFinance.yearly_sales || {};

                      // Filter helpers
                      const filteredDailyEntries = Object.entries(ds).filter(([d]) => {
                        if (rootFinanceFilter === 'today') return d === today;
                        if (rootFinanceFilter === 'month') return d.startsWith(thisMonth);
                        if (rootFinanceFilter === 'year') return d.startsWith(thisYear);
                        return true;
                      });

                      const filteredMonthlyEntries = Object.entries(ms).filter(([m]) => {
                        if (rootFinanceFilter === 'today' || rootFinanceFilter === 'month') return m === thisMonth;
                        if (rootFinanceFilter === 'year') return m.startsWith(thisYear);
                        return true;
                      });

                      const filteredTotal = filteredDailyEntries.reduce((sum, [, v]) => sum + v, 0);
                      const ordersToday = ds[today] || 0;
                      const ordersThisMonth = ms[thisMonth] || 0;
                      const ordersThisYear = ys[thisYear] || 0;

                      return (
                        <>
                          {/* KPI Cards */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-[#FFF4EC] to-[#FFE5CC] p-5 rounded-xl border border-orange-100">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Net Income</p>
                              <p className="text-2xl font-bold text-[#F47B20] mt-1">₦{rootFinance.total_income?.toLocaleString()}</p>
                            </div>
                            <div className="bg-[#FAF8F5] p-5 rounded-xl border border-[#E8E0D8]">
                              <p className="text-[10px] font-bold text-rose-400 uppercase tracking-wider">Total Refunds</p>
                              <p className="text-2xl font-bold text-rose-600 mt-1">₦{rootFinance.total_refunds?.toLocaleString()}</p>
                            </div>
                            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                              <h4 className="text-xs font-bold text-gray-500 uppercase">Today</h4>
                              <p className="text-2xl font-bold text-[#1A1A2E] mt-1">₦{ordersToday.toLocaleString()}</p>
                              <p className="text-[10px] text-gray-400 mt-1">{today}</p>
                            </div>
                            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                              <h4 className="text-xs font-bold text-gray-500 uppercase">This Month</h4>
                              <p className="text-2xl font-bold text-[#1A1A2E] mt-1">₦{ordersThisMonth.toLocaleString()}</p>
                              <p className="text-[10px] text-gray-400 mt-1">{thisMonth}</p>
                            </div>
                            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                              <h4 className="text-xs font-bold text-gray-500 uppercase">This Year</h4>
                              <p className="text-2xl font-bold text-[#1A1A2E] mt-1">₦{ordersThisYear.toLocaleString()}</p>
                              <p className="text-[10px] text-gray-400 mt-1">{thisYear}</p>
                            </div>
                          </div>

                          {/* Filtered Total Banner */}
                          {rootFinanceFilter !== 'all' && (
                            <div className="flex items-center gap-3 bg-[#F47B20]/10 border border-[#F47B20]/20 rounded-xl p-4">
                              <span className="text-[#F47B20] text-xl font-bold">₦{filteredTotal.toLocaleString()}</span>
                              <span className="text-sm text-gray-600">
                                income for <strong>{filterOptions.find(o => o.id === rootFinanceFilter)?.label}</strong>
                              </span>
                            </div>
                          )}

                          {/* Daily Line Chart */}
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-4">
                              Daily Sales — {filterOptions.find(o => o.id === rootFinanceFilter)?.label}
                            </h4>
                            <div className="h-72 w-full">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={filteredDailyEntries.map(([date, sales]) => ({ date, sales }))}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                  <XAxis dataKey="date" tick={{fontSize: 11}} />
                                  <YAxis tick={{fontSize: 11}} width={85} tickFormatter={val => '₦' + val.toLocaleString()} />
                                  <RechartsTooltip formatter={(value) => ['₦' + value.toLocaleString(), 'Sales']} />
                                  <Line type="monotone" dataKey="sales" stroke="#F47B20" strokeWidth={3} dot={{r: 4}} activeDot={{r: 6}} />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </div>

                          {/* Monthly Bar Chart */}
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-4">Monthly Income Breakdown</h4>
                            <div className="h-72 w-full">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={filteredMonthlyEntries.map(([month, sales]) => ({ month, sales }))}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                  <XAxis dataKey="month" tick={{fontSize: 11}} />
                                  <YAxis tick={{fontSize: 11}} width={85} tickFormatter={val => '₦' + val.toLocaleString()} />
                                  <RechartsTooltip formatter={(value) => ['₦' + value.toLocaleString(), 'Income']} />
                                  <Bar dataKey="sales" fill="#1A1A2E" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>

                          {/* Daily Breakdown Table */}
                          <div>
                            <h4 className="text-sm font-bold text-gray-700 mb-3">Day-by-Day Breakdown</h4>
                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                              <table className="w-full text-sm text-left">
                                <thead>
                                  <tr className="bg-gray-50 border-b border-gray-100 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                                    <th className="p-3 whitespace-nowrap">Date</th>
                                    <th className="p-3 whitespace-nowrap">Day</th>
                                    <th className="p-3 whitespace-nowrap">Revenue</th>
                                    <th className="p-3 whitespace-nowrap">% of Period</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredDailyEntries.length === 0 ? (
                                    <tr><td colSpan="4" className="p-6 text-center text-gray-400">No data for this period.</td></tr>
                                  ) : filteredDailyEntries.sort(([a], [b]) => b.localeCompare(a)).map(([date, sales]) => (
                                    <tr key={date} className={`border-b border-gray-50 hover:bg-gray-50 ${date === today ? 'bg-orange-50' : ''}`}>
                                      <td className="p-3 font-mono text-xs text-gray-600 whitespace-nowrap">{date}</td>
                                      <td className="p-3 text-gray-700 whitespace-nowrap">{new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' })}</td>
                                      <td className="p-3 font-bold text-gray-900 whitespace-nowrap">₦{sales.toLocaleString()}</td>
                                      <td className="p-3 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                            <div className="bg-[#F47B20] h-1.5 rounded-full" style={{width: filteredTotal ? `${(sales / filteredTotal * 100).toFixed(0)}%` : '0%'}} />
                                          </div>
                                          <span className="text-xs text-gray-500">{filteredTotal ? (sales / filteredTotal * 100).toFixed(1) : 0}%</span>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                {filteredDailyEntries.length > 0 && (
                                  <tfoot>
                                    <tr className="bg-gray-50 border-t-2 border-gray-200">
                                      <td colSpan="2" className="p-3 font-bold text-gray-700">Period Total</td>
                                      <td className="p-3 font-bold text-[#F47B20]">₦{filteredTotal.toLocaleString()}</td>
                                      <td className="p-3 text-xs text-gray-500">{filteredDailyEntries.length} day{filteredDailyEntries.length !== 1 ? 's' : ''}</td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                          </div>

                          {/* Download Button */}
                          <div className="flex justify-end">
                            <button
                              onClick={async () => {
                                setRootLoading(true);
                                try {
                                  await rootDownloadFinancePdf();
                                  toast.success('Finance report downloaded!');
                                } catch(e) {
                                  toast.error('Failed to generate PDF.');
                                } finally {
                                  setRootLoading(false);
                                }
                              }}
                              disabled={rootLoading}
                              className="flex items-center gap-2 bg-[#F47B20] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-60"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                              {rootLoading ? 'Generating...' : 'Download Income Report PDF'}
                            </button>
                          </div>
                        </>
                      );
                    })() : (
                      <div className="text-center text-sm text-gray-400 py-10">Loading Finance Data...</div>
                    )}
                  </div>
                );
              })()}

              {/* Activity Logs */}
              {rootCmsTab === 'activity_logs' && (
                <div className="bg-white rounded-2xl border border-[#E8E0D8] shadow-sm">
                  <SectionHeader title="Activity Logs" />
                  <div className="p-8 flex flex-col items-center justify-center gap-4">
                    <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                    </div>
                    <div className="text-center">
                      <h4 className="font-bold text-gray-800 mb-1">Activity Logs PDF</h4>
                      <p className="text-sm text-gray-500">Download a full PDF report of the last 200 system actions, including user, role, action type, description, and IP address.</p>
                    </div>
                    <button
                      onClick={async () => {
                        setRootLoading(true);
                        try {
                          await rootDownloadActivityPdf();
                          toast.success('Activity report downloaded!');
                        } catch(e) {
                          toast.error('Failed to generate PDF.');
                        } finally {
                          setRootLoading(false);
                        }
                      }}
                      disabled={rootLoading}
                      className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-purple-700 transition-colors disabled:opacity-60"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      {rootLoading ? 'Generating...' : 'Download Activity Report PDF'}
                    </button>
                  </div>
                </div>
              )}

              {/* System Error Logs */}
              {rootCmsTab === 'logs' && (() => {
                const actionColors = {
                  login: 'bg-green-100 text-green-700',
                  logout: 'bg-gray-100 text-gray-600',
                  login_failed: 'bg-red-100 text-red-700',
                  register: 'bg-blue-100 text-blue-700',
                  password_reset: 'bg-yellow-100 text-yellow-700',
                  create_product: 'bg-purple-100 text-purple-700',
                  update_product: 'bg-indigo-100 text-indigo-700',
                  delete_product: 'bg-red-100 text-red-700',
                  update_order: 'bg-orange-100 text-orange-700',
                  purge_orders: 'bg-red-200 text-red-800',
                };
                return (
                  <div className="bg-white rounded-2xl border border-[#E8E0D8] shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-gray-100">
                      <div>
                        <h3 className="font-bold text-base text-gray-800">Activity Log</h3>
                        <p className="text-xs text-gray-400 mt-0.5">All logins, actions and events — last 100 entries</p>
                      </div>
                      <button
                        onClick={() => rootGetActivityLogs().then(setRootActivityLogs).catch(() => {})}
                        className="px-4 py-2 text-xs font-bold bg-[#1A1A2E] text-white rounded-xl hover:bg-black transition-colors"
                      >
                        ↻ Refresh
                      </button>
                    </div>
                    {rootActivityLogs.length === 0 ? (
                      <div className="p-10 text-center text-gray-400 text-sm">No activity recorded yet.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm min-w-[640px]">
                          <thead className="bg-gray-50 border-b border-gray-100 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                            <tr>
                              <th className="px-4 py-3">Time</th>
                              <th className="px-4 py-3">Action</th>
                              <th className="px-4 py-3">User</th>
                              <th className="px-4 py-3">Description</th>
                              <th className="px-4 py-3">IP Address</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {rootActivityLogs.map((log, idx) => (
                              <tr key={log.id || idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">
                                  {log.created_at ? new Date(log.created_at).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${actionColors[log.action] || 'bg-gray-100 text-gray-600'}`}>
                                    {log.action?.replace(/_/g, ' ')}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-700">
                                  {log.user ? (
                                    <div>
                                      <div className="font-semibold">{log.user.name}</div>
                                      <div className="text-gray-400">{log.user.email}</div>
                                    </div>
                                  ) : <span className="text-gray-400 italic">Guest</span>}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-600 max-w-[220px] truncate">{log.description}</td>
                                <td className="px-4 py-3 text-xs text-gray-400 font-mono">{log.ip_address || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Data Purge */}
              {rootCmsTab === 'purge' && (
                <div className="bg-white rounded-2xl border border-[#E8E0D8] p-6 shadow-sm space-y-4">
                  <h3 className="font-bold text-sm text-gray-800">Data Maintenance</h3>
                  <p className="text-xs text-red-500 font-semibold">Warning: These actions are irreversible. Proceed with caution.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { type: 'cache', label: 'Clear App Cache', desc: 'Flush all cached data' },
                      { type: 'logs', label: 'Clear Log File', desc: 'Wipe the laravel.log file' },
                      { type: 'sessions', label: 'Purge Sessions', desc: 'Force all users to re-login' },
                      { type: 'users', label: 'Purge Non-Root Users', desc: 'Delete all users except root', danger: true },
                      { type: 'database', label: 'Purge Full Database', desc: 'Wipe all data from the database except users', danger: true },
                      { type: 'all', label: 'Clear Everything', desc: 'Cache + sessions + logs', danger: true },
                      { type: 'orders', label: 'Purge All Orders', desc: 'Delete ALL orders and order items permanently', danger: true },
                    ].map(a => (
                      <div key={a.type} className={`p-4 rounded-xl border ${
                        a.danger ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <p className="font-bold text-sm text-gray-800 mb-0.5">{a.label}</p>
                        <p className="text-xs text-gray-400 mb-3">{a.desc}</p>
                        <button
                          disabled={rootLoading}
                          onClick={() => {
                            setConfirmModalState({
                              isOpen: true,
                              title: `Confirm: ${a.label}?`,
                              message: `Are you sure you want to perform: ${a.label}?`,
                              confirmText: 'Execute',
                              onConfirm: async () => {
                                if (a.type === 'orders') {
                                  const res = await purgeAllOrders();
                                  toast.success(res.message || 'Orders purged');
                                  setOrders([]);
                                } else {
                                  setRootActionAuthModal({
                                    isOpen: true,
                                    step: 0,
                                    password: '',
                                    otp: '',
                                    actionName: a.label,
                                    error: '',
                                    isProcessing: false,
                                    onSuccess: async (otp) => {
                                      if (a.type === 'users') {
                                        await doRootAction(() => rootPurgeUsers(otp), 'Users purged!');
                                      } else if (a.type === 'database') {
                                        await doRootAction(() => rootPurgeDatabase(otp), 'Database fully purged!');
                                      } else {
                                        await doRootAction(() => rootPurgeData(a.type, otp), `${a.label} completed!`);
                                      }
                                    }
                                  });
                                }
                              }
                            });
                          }}
                          className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                            a.danger ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-[#1A1A2E] text-white hover:bg-black'
                          }`}
                        >
                          {rootLoading ? 'Running...' : 'Execute'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Backup & Restore */}
              {rootCmsTab === 'backup' && (
                <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100">
                  <h3 className="text-xl font-bold text-[#1A1A2E] mb-6">Database Backup & Restore</h3>
                  <div className="space-y-8">
                    <div>
                      <h4 className="font-bold text-gray-800 mb-2">Export Data</h4>
                      <p className="text-sm text-gray-500 mb-4">Download a complete copy of all your database records (Products, Orders, Users, etc.) as a JSON file.</p>
                      <button
                        onClick={async () => {
                          try {
                            const res = await rootExportDatabase();
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `manna_backup_${new Date().toISOString().slice(0, 10)}.json`);
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                          } catch (err) {
                            setRootNotice('Error: Failed to export database.');
                          }
                        }}
                        className="px-6 py-3 bg-[#1A1A2E] text-white rounded-xl font-bold shadow-sm hover:bg-black transition-colors"
                      >
                        Download Backup
                      </button>
                    </div>
                    <div className="border-t border-gray-100 pt-8">
                      <h4 className="font-bold text-gray-800 mb-2">Restore Data</h4>
                      <p className="text-sm text-gray-500 mb-4">Upload a previously downloaded JSON backup. <b>WARNING:</b> This will erase all current database records and replace them with the backup data!</p>
                      <input
                        type="file"
                        id="backup-upload"
                        className="hidden"
                        accept=".json"
                        onChange={async (e) => {
                          if (!e.target.files[0]) return;
                          if (!window.confirm("Are you sure? This will wipe the current database!")) return;
                          try {
                            await rootImportDatabase(e.target.files[0]);
                            setRootNotice('Database restored successfully! Please refresh the page.');
                          } catch (err) {
                            setRootNotice('Error: Failed to restore database. Ensure the file is valid.');
                          }
                          e.target.value = '';
                        }}
                      />
                      <button
                        onClick={() => document.getElementById('backup-upload').click()}
                        className="px-6 py-3 bg-red-50 text-red-600 rounded-xl font-bold shadow-sm hover:bg-red-100 border border-red-200 transition-colors"
                      >
                        Upload & Restore Backup
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Broadcast */}
              {rootCmsTab === 'broadcast' && (
                <div className="bg-white rounded-2xl border border-[#E8E0D8] p-6 shadow-sm space-y-4">
                  <h3 className="font-bold text-sm text-gray-800">System-Wide Broadcast</h3>
                  <p className="text-xs text-gray-400">Send an email to ALL users including admins and staff.</p>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Subject</label>
                    <input
                      className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]"
                      value={rootMsg.subject}
                      onChange={e => setRootMsg(p => ({ ...p, subject: e.target.value }))}
                      placeholder="e.g. Special Offer for You!"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Message Body</label>
                    <textarea
                      rows={5}
                      className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none outline-none focus:border-[#F47B20]"
                      value={rootMsg.message}
                      onChange={e => setRootMsg(p => ({ ...p, message: e.target.value }))}
                      placeholder="Write your message here..."
                    />
                  </div>
                  <button
                    disabled={rootLoading || !rootMsg.subject || !rootMsg.message}
                    onClick={() => {
                      setConfirmModalState({
                        isOpen: true,
                        title: 'Send Mass Message',
                        message: 'Send this message to ALL system users?',
                        confirmText: 'Send',
                        onConfirm: async () => {
                          await doRootAction(() => rootBroadcast(rootMsg.subject, rootMsg.message), 'Broadcast sent!');
                          setRootMsg({ subject: '', message: '' });
                        }
                      });
                    }}
                    className="bg-[#F47B20] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-50"
                  >
                    {rootLoading ? 'Sending...' : 'Send Broadcast'}
                  </button>
                </div>
              )}

              {/* Reports */}
              {rootCmsTab === 'report' && (
                <div className="bg-white rounded-2xl border border-[#E8E0D8] p-6 shadow-sm space-y-4">
                  <h3 className="font-bold text-sm text-gray-800">System Reports</h3>
                  <p className="text-xs text-gray-400">Download a full CSV report covering all orders, users, and products.</p>
                  <div className="flex gap-3 items-center p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex-1">
                      <p className="font-bold text-sm text-gray-800">Full System Report</p>
                      <p className="text-xs text-gray-400">Orders · Customers · Products · Revenue</p>
                    </div>
                    <button
                      onClick={rootDownloadReport}
                      className="bg-[#1A1A2E] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-black transition-colors"
                    >
                      Download CSV
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </main>

      {/* ── FORCE PASSWORD CHANGE MODAL ── */}
      {showChangePwd && (
        <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-8 space-y-5 shadow-2xl">
            <div className="text-center">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              </div>
              <h2 className="font-black text-xl text-[#1A1A2E]">Change Your Password</h2>
              <p className="text-sm text-gray-400 mt-1">You must set a new password before continuing. Default passwords are not allowed.</p>
            </div>
            {changePwdError && (
              <div className="bg-red-50 text-red-600 text-sm px-4 py-2.5 rounded-xl border border-red-200">{changePwdError}</div>
            )}
            <form onSubmit={handleForcedPwdChange} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block">New Password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]"
                  value={changePwdForm.password}
                  onChange={e => setChangePwdForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Min 8 characters"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block">Confirm Password</label>
                <input
                  type="password"
                  required
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]"
                  value={changePwdForm.confirm}
                  onChange={e => setChangePwdForm(p => ({ ...p, confirm: e.target.value }))}
                  placeholder="Repeat new password"
                />
              </div>
              <button
                type="submit"
                disabled={changePwdLoading}
                className="w-full py-3 bg-[#F47B20] text-white rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-60"
              >
                {changePwdLoading ? 'Changing...' : 'Set New Password & Continue'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════ MODALS ═══════════════════════ */}

      {rootActionAuthModal.isOpen && (
        <Modal onClose={() => !rootActionAuthModal.isProcessing && setRootActionAuthModal(prev => ({ ...prev, isOpen: false }))} title="Root Authentication Required">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              You are about to perform a sensitive action: <strong>{rootActionAuthModal.actionName}</strong>.
            </p>
            {rootActionAuthModal.error && <p className="text-sm text-red-600">{rootActionAuthModal.error}</p>}
            
            {rootActionAuthModal.step === 0 && (
              <>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Your Password</label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    value={rootActionAuthModal.password}
                    onChange={e => setRootActionAuthModal(prev => ({ ...prev, password: e.target.value }))}
                  />
                </div>
                <ModalFooter
                  onCancel={() => setRootActionAuthModal(prev => ({ ...prev, isOpen: false }))}
                  onSave={async () => {
                    setRootActionAuthModal(prev => ({ ...prev, isProcessing: true, error: '' }));
                    try {
                      await rootRequestActionAuth(rootActionAuthModal.password, rootActionAuthModal.actionName);
                      setRootActionAuthModal(prev => ({ ...prev, step: 1, isProcessing: false }));
                      toast.success('OTP sent to your email.');
                    } catch (e) {
                      setRootActionAuthModal(prev => ({ ...prev, isProcessing: false, error: e?.response?.data?.message || 'Authentication failed' }));
                    }
                  }}
                  saveLabel={rootActionAuthModal.isProcessing ? 'Verifying...' : 'Continue'}
                  disabled={rootActionAuthModal.isProcessing || !rootActionAuthModal.password}
                />
              </>
            )}

            {rootActionAuthModal.step === 1 && (
              <>
                <p className="text-xs text-gray-500">An OTP has been sent to your email. Please enter it below.</p>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">OTP</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    value={rootActionAuthModal.otp}
                    onChange={e => setRootActionAuthModal(prev => ({ ...prev, otp: e.target.value }))}
                    maxLength={6}
                  />
                </div>
                <ModalFooter
                  onCancel={() => setRootActionAuthModal(prev => ({ ...prev, isOpen: false }))}
                  onSave={async () => {
                    setRootActionAuthModal(prev => ({ ...prev, isProcessing: true, error: '' }));
                    try {
                      if (rootActionAuthModal.onSuccess) {
                        await rootActionAuthModal.onSuccess(rootActionAuthModal.otp);
                      }
                      setRootActionAuthModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
                    } catch (e) {
                      setRootActionAuthModal(prev => ({ ...prev, isProcessing: false, error: e?.response?.data?.message || 'Action failed' }));
                    }
                  }}
                  saveLabel={rootActionAuthModal.isProcessing ? 'Executing...' : 'Execute Action'}
                  disabled={rootActionAuthModal.isProcessing || rootActionAuthModal.otp.length !== 6}
                />
              </>
            )}
          </div>
        </Modal>
      )}

      <ConfirmModal
        isOpen={confirmModalState.isOpen}
        title={confirmModalState.title}
        message={confirmModalState.message}
        confirmText={confirmModalState.confirmText}
        isProcessing={confirmModalState.isProcessing}
        onClose={() => setConfirmModalState({ ...confirmModalState, isOpen: false })}
        onConfirm={async () => {
          setConfirmModalState({ ...confirmModalState, isProcessing: true });
          try {
            if (confirmModalState.onConfirm) await confirmModalState.onConfirm();
          } catch(e){}
          setConfirmModalState(prev => ({ ...prev, isOpen: false, isProcessing: false }));
        }}
      />

      {/* ── REVENUE MODAL ── */}
      {isRevenueModalOpen && (
        <Modal onClose={() => setIsRevenueModalOpen(false)} title="Revenue Breakdown">
          <div className="space-y-4" id="revenue-report">
            {(() => {
              let day = 0, week = 0, month = 0, year = 0, total = 0;
              const now = new Date();
              orders.forEach(o => {
                const d = new Date(o.created_at || o.date);
                if (!isNaN(d.getTime())) {
                  const amt = Number(o.price || o.total || 0);
                  total += amt;
                  const diffDays = (now - d) / (1000 * 60 * 60 * 24);
                  if (diffDays <= 1) day += amt;
                  if (diffDays <= 7) week += amt;
                  if (diffDays <= 30) month += amt;
                  if (diffDays <= 365) year += amt;
                }
              });
              return (
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Last 24 Hours" value={`₦${day.toLocaleString()}`} />
                  <InfoField label="Last 7 Days" value={`₦${week.toLocaleString()}`} />
                  <InfoField label="Last 30 Days" value={`₦${month.toLocaleString()}`} />
                  <InfoField label="Last 365 Days" value={`₦${year.toLocaleString()}`} />
                  <div className="col-span-2 pt-2 border-t border-gray-100">
                    <InfoField label="All Time Total" value={`₦${total.toLocaleString()}`} className="text-xl text-[#F47B20]" />
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="mt-6 flex justify-end gap-2.5">
            <button onClick={() => setIsRevenueModalOpen(false)} className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200">Close</button>
            <button onClick={() => {
              const el = document.getElementById('revenue-report');
              const printWindow = window.open('', '_blank');
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Revenue Report</title>
                    <style>
                      body { font-family: sans-serif; padding: 40px; color: #1A1A2E; }
                      h1 { color: #F47B20; border-bottom: 2px solid #FEF0E6; padding-bottom: 10px; }
                      .row { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #f3f4f6; }
                      .label { font-size: 12px; text-transform: uppercase; color: #6b7280; font-weight: bold; }
                      .val { font-size: 18px; font-weight: bold; }
                      .total { font-size: 24px; color: #F47B20; }
                    </style>
                  </head>
                  <body>
                    <h1>Manna Bridal Revenue Report</h1>
                    <p>Generated on: ${new Date().toLocaleString()}</p>
                    <div style="margin-top: 40px;">
                      ${el.innerHTML.replace(/className="[^"]*"/g, '').replace(/class="[^"]*"/g, '')}
                    </div>
                    <script>
                      setTimeout(() => { window.print(); window.close(); }, 500);
                    </script>
                  </body>
                </html>
              `);
              printWindow.document.close();
            }} className="px-5 py-2.5 bg-[#F47B20] text-white rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors">
              Export PDF
            </button>
          </div>
        </Modal>
      )}

      {/* ── ORDERS MODAL ── */}
      {isOrdersModalOpen && (
        <Modal onClose={() => setIsOrdersModalOpen(false)} title="Orders Breakdown">
          <div className="space-y-4">
            {(() => {
              let day = 0, week = 0, month = 0, year = 0;
              const now = new Date();
              orders.forEach(o => {
                const d = new Date(o.created_at || o.date);
                if (!isNaN(d.getTime())) {
                  const diffDays = (now - d) / (1000 * 60 * 60 * 24);
                  if (diffDays <= 1) day++;
                  if (diffDays <= 7) week++;
                  if (diffDays <= 30) month++;
                  if (diffDays <= 365) year++;
                }
              });
              return (
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Last 24 Hours" value={day} />
                  <InfoField label="Last 7 Days" value={week} />
                  <InfoField label="Last 30 Days" value={month} />
                  <InfoField label="Last 365 Days" value={year} />
                  <div className="col-span-2 pt-2 border-t border-gray-100">
                    <InfoField label="All Time Total" value={orders.length} className="text-xl text-[#F47B20]" />
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={() => setIsOrdersModalOpen(false)} className="px-5 py-2.5 bg-[#F47B20] text-white rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors">Close</button>
          </div>
        </Modal>
      )}

      {/* ── ORDER DETAIL MODAL ── */}
      {isOrderModalOpen && selectedOrder && (
        <Modal onClose={() => { setIsOrderModalOpen(false); setSelectedOrder(null); }} title={`Order ${selectedOrder.id}`}>
          <div className="space-y-5">
            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <InfoField label="Customer" value={selectedOrder.customer} />
              <InfoField label="Email" value={selectedOrder.email} />
              <InfoField label="Phone" value={selectedOrder.phone} />
              <InfoField label="Product" value={selectedOrder.product} />
              {currentRole !== 'staff' && (
                <>
                  <InfoField label="Subtotal" value={`₦${Number(selectedOrder.subtotal || 0).toLocaleString()}`} />
                  <InfoField label="Shipping Fee" value={`₦${Number(selectedOrder.shipping_fee || 0).toLocaleString()}`} />
                  <InfoField label="Total Amount" value={selectedOrder.amount} />
                </>
              )}
              <InfoField label="Order Date" value={selectedOrder.date} />
              <InfoField label="Delivery Address" value={selectedOrder.address} className="sm:col-span-2" />
            </div>

            {/* Ordered Items */}
            {selectedOrder.items && selectedOrder.items.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase">Ordered Items</label>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, i) => {
                    const itemImg = item.image_url && !item.image_url.match(/\.(mp4|webm|ogg)$/i)
                      ? item.image_url
                      : (Array.isArray(item.product?.images)
                          ? item.product.images.find(img => !img.match(/\.(mp4|webm|ogg)$/i))
                          : null);
                    return (
                      <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-gray-200 shrink-0">
                          {itemImg
                            ? <img src={itemImg} alt={item.product_name} className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setFullscreenImage(itemImg)} />
                            : <div className="w-full h-full flex items-center justify-center text-gray-300 text-xl">🛍</div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 text-sm truncate">{item.product_name}</p>
                          <p className="text-xs text-gray-400">Qty: {item.quantity} · ₦{Number(item.price || 0).toLocaleString()}{item.size ? ` · Size: ${item.size}` : ''}{item.color ? ` · Color: ${item.color}` : ''}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-400 uppercase">Internal Notes</label>
              <textarea
                rows={2}
                value={selectedOrder.notes || ''}
                onChange={e => setSelectedOrder({ ...selectedOrder, notes: e.target.value })}
                readOnly={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:border-[#F47B20] outline-none readOnly:bg-gray-50 readOnly:text-gray-800"
                placeholder="Add private notes about this order…"
              />
            </div>

            {/* Status History / Timeline */}
            <div className="space-y-2 mt-4 p-4 bg-gray-50 border border-gray-100 rounded-xl">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Status History</label>
              <div className="space-y-3">
                {selectedOrder.status_history?.length > 0 ? (
                  selectedOrder.status_history.map((hist, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm border-l-2 border-[#F47B20] pl-3">
                      <div>
                        <span className="font-bold text-gray-700 capitalize">{hist.status}</span>
                        {hist.note && <span className="ml-2 text-xs text-gray-500">({hist.note})</span>}
                      </div>
                      <span className="text-gray-500 text-xs">
                        {new Date(hist.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 italic">No history available.</p>
                )}
              </div>
            </div>

            {/* Status update */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-400 uppercase">Order Status</label>
              <select
                value={selectedOrder.status || 'pending'}
                onChange={e => setSelectedOrder({ ...selectedOrder, status: e.target.value })}
                disabled={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white font-bold text-[#F47B20] focus:border-[#F47B20] outline-none disabled:bg-gray-50 disabled:text-[#1A1A2E] disabled:opacity-100"
              >
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="ready_for_pickup">Ready for Pickup</option>
                <option value="picked_up">Picked Up</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {selectedOrder.status === 'picked_up' && (
              <div className="space-y-3 p-4 bg-[#FEF0E6]/50 border border-[#F47B20]/20 rounded-xl">
                <p className="text-xs font-bold text-[#F47B20] uppercase tracking-wider">Pickup Details</p>
                <input
                  type="text"
                  placeholder="Name of person who picked up"
                  value={selectedOrder.pickup_info?.name || ''}
                  onChange={e => setSelectedOrder({ ...selectedOrder, pickup_info: { ...selectedOrder.pickup_info, name: e.target.value } })}
                  readOnly={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#F47B20] readOnly:bg-gray-50 readOnly:text-gray-800"
                />
                <input
                  type="text"
                  placeholder="Phone number of person who picked up"
                  value={selectedOrder.pickup_info?.phone || ''}
                  onChange={e => setSelectedOrder({ ...selectedOrder, pickup_info: { ...selectedOrder.pickup_info, phone: e.target.value } })}
                  readOnly={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#F47B20] readOnly:bg-gray-50 readOnly:text-gray-800"
                />
                <input
                  type="text"
                  placeholder="Timestamp (e.g. 10:00 AM, 12th Aug)"
                  value={selectedOrder.pickup_info?.timestamp || ''}
                  onChange={e => setSelectedOrder({ ...selectedOrder, pickup_info: { ...selectedOrder.pickup_info, timestamp: e.target.value } })}
                  readOnly={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#F47B20] readOnly:bg-gray-50 readOnly:text-gray-800"
                />
              </div>
            )}

            {selectedOrder.status === 'shipped' && (
              <div className="space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Shipping Details (Emailed to Customer)</p>
                <input
                  type="text"
                  placeholder="Courier Name (e.g. DHL)"
                  value={selectedOrder.courier_name || ''}
                  onChange={e => setSelectedOrder({ ...selectedOrder, courier_name: e.target.value })}
                  readOnly={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#F47B20] readOnly:bg-gray-50 readOnly:text-gray-800"
                />
                <input
                  type="text"
                  placeholder="Courier Number"
                  value={selectedOrder.tracking_number || ''}
                  onChange={e => setSelectedOrder({ ...selectedOrder, tracking_number: e.target.value })}
                  readOnly={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#F47B20] readOnly:bg-gray-50 readOnly:text-gray-800"
                />
                <input
                  type="text"
                  placeholder="Tracking Link / URL (Optional)"
                  value={selectedOrder.tracking_url || ''}
                  onChange={e => setSelectedOrder({ ...selectedOrder, tracking_url: e.target.value })}
                  readOnly={orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'}
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#F47B20] readOnly:bg-gray-50 readOnly:text-gray-800"
                />
              </div>
            )}

            <div className="flex flex-col gap-3 pt-2">
              {(orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled') && (
                <p className="text-xs text-center text-gray-400 italic bg-gray-50 rounded-lg py-2 px-3">
                  This order is completed and cannot be updated.
                </p>
              )}
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => {
                    const items = (selectedOrder.items || []).map((item, i) => `
                      <tr style="border-bottom:1px solid #f0e8e0;">
                        <td style="padding:10px 8px;font-size:13px;">${item.product_name || item.name || 'Item'}</td>
                        <td style="padding:10px 8px;font-size:13px;text-align:center;">${item.quantity || item.qty || 1}</td>
                        <td style="padding:10px 8px;font-size:13px;color:#888;">${[item.size ? `Size: ${item.size}` : '', item.color ? `Color: ${item.color}` : ''].filter(Boolean).join(' · ') || '—'}</td>
                      </tr>`).join('');
                    const printWindow = window.open('', '_blank', 'width=420,height=600');
                    printWindow.document.write(`
                      <!DOCTYPE html><html><head>
                      <title>Delivery Slip – ${selectedOrder.id}</title>
                      <style>
                        * { box-sizing: border-box; margin: 0; padding: 0; }
                        @page { size: auto; margin: 6mm; }
                        body { font-family: Arial, sans-serif; color: #111; background: #fff; font-size: 10px; width: 100%; max-width: 100%; }
                        .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #F47B20; padding-bottom: 6px; margin-bottom: 8px; }
                        .brand { font-size: 14px; font-weight: bold; color: #F47B20; }
                        .brand-sub { font-size: 8px; color: #888; margin-top: 1px; }
                        .order-ref { text-align: right; font-size: 8px; color: #555; }
                        .order-ref strong { display: block; font-size: 13px; color: #111; font-weight: bold; }
                        .badge { display: inline-block; padding: 1px 6px; border: 1px solid #F47B20; border-radius: 3px; font-size: 7px; font-weight: bold; color: #c05c00; text-transform: uppercase; margin-top: 3px; }
                        .section { margin-bottom: 7px; }
                        .section-title { font-size: 7px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #F47B20; margin-bottom: 4px; border-bottom: 1px solid #fde8d8; padding-bottom: 2px; }
                        .detail-row { display: flex; gap: 4px; font-size: 9px; margin-bottom: 2px; line-height: 1.3; }
                        .detail-label { font-weight: bold; color: #444; min-width: 52px; flex-shrink: 0; }
                        table { width: 100%; border-collapse: collapse; }
                        th { font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; color: #777; padding: 3px 4px; text-align: left; border-bottom: 1px solid #eee; }
                        td { font-size: 9px; padding: 3px 4px; border-bottom: 1px solid #f5f5f5; }
                        td:nth-child(2) { text-align: center; }
                        .footer { margin-top: 10px; padding-top: 5px; border-top: 1px dashed #ccc; text-align: center; font-size: 7px; color: #aaa; }
                        @media print { body { width: 100%; max-width: 100%; } }
                      </style>
                      </head><body>
                      <div class="header">
                        <div>
                          <div class="brand">Manna Bridal</div>
                          <div class="brand-sub">Premium Bridal Collections</div>
                        </div>
                        <div class="order-ref">
                          <span>Order Ref</span>
                          <strong>${selectedOrder.id}</strong>
                          <div><span class="badge">${(selectedOrder.status || 'pending').toUpperCase()}</span></div>
                        </div>
                      </div>

                      <div class="section">
                        <div class="section-title">Deliver To</div>
                        <div class="detail-row"><span class="detail-label">Name:</span> ${selectedOrder.customer}</div>
                        <div class="detail-row"><span class="detail-label">Phone:</span> ${selectedOrder.phone || '—'}</div>
                        <div class="detail-row"><span class="detail-label">Address:</span> ${selectedOrder.address || '—'}</div>
                      </div>

                      <div class="section">
                        <div class="section-title">Items Ordered</div>
                        <table>
                          <thead><tr>
                            <th>Product</th>
                            <th>Qty</th>
                            <th>Options</th>
                          </tr></thead>
                          <tbody>${items}</tbody>
                        </table>
                      </div>

                      ${(selectedOrder.courier_name || selectedOrder.tracking_number) ? `
                      <div class="section">
                        <div class="section-title">Courier Info</div>
                        ${selectedOrder.courier_name ? `<div class="detail-row"><span class="detail-label">Courier:</span> ${selectedOrder.courier_name}</div>` : ''}
                        ${selectedOrder.tracking_number ? `<div class="detail-row"><span class="detail-label">Courier No:</span> ${selectedOrder.tracking_number}</div>` : ''}
                      </div>` : ''}

                      <div class="footer">Manna Bridal · ${new Date().toLocaleDateString()}</div>
                      <script>setTimeout(() => { window.print(); window.close(); }, 400);</script>
                      </body></html>`);
                    printWindow.document.close();
                  }}
                  className="px-4 py-2.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-sm font-bold hover:bg-gray-100 transition-colors flex items-center gap-1.5"
                  title="Print delivery slip (no prices)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Slip
                </button>
                <button 
                  onClick={() => handleChatCustomer(selectedOrder)} 
                  className="px-4 py-2.5 bg-blue-50 text-blue-600 border border-blue-200 rounded-xl text-sm font-bold hover:bg-blue-100 transition-colors"
                >
                  Message Customer
                </button>
                <button onClick={() => { setIsOrderModalOpen(false); setSelectedOrder(null); }} className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200">Close</button>
                <button 
                  onClick={handleUpdateOrder} 
                  disabled={isUpdatingOrder || orders.find(o => o.id === selectedOrder.id)?.status === 'delivered' || orders.find(o => o.id === selectedOrder.id)?.status === 'cancelled'} 
                  className="px-5 py-2.5 bg-[#F47B20] text-white rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingOrder ? 'Updating...' : 'Update Order'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── ADD PRODUCT MODAL ── */}
      {isProductModalOpen && (
        <Modal onClose={() => setIsProductModalOpen(false)} title="Add Product">
          <ProductForm data={newProduct} setData={setNewProduct} categories={categories} />
          <ModalFooter onCancel={() => setIsProductModalOpen(false)} onSave={handleAddProduct} saveLabel={isSavingProduct ? 'Saving...' : 'Save Product'} disabled={isSavingProduct} />
        </Modal>
      )}

      {/* ── EDIT PRODUCT MODAL ── */}
      {isEditProductOpen && editingProduct && (
        <Modal onClose={() => { setIsEditProductOpen(false); setEditingProduct(null); }} title="Edit Product">
          <ProductForm data={editingProduct} setData={setEditingProduct} categories={categories} />
          <ModalFooter onCancel={() => { setIsEditProductOpen(false); setEditingProduct(null); }} onSave={handleUpdateProduct} saveLabel="Update Product" loading={isSavingProduct} />
        </Modal>
      )}

      {/* ── ADD BANNER MODAL ── */}
      {isBannerModalOpen && (
        <Modal onClose={() => setIsBannerModalOpen(false)} title="New Promo Banner">
          <BannerForm data={newBanner} setData={setNewBanner} categories={categories} />
          <ModalFooter onCancel={() => setIsBannerModalOpen(false)} onSave={handleAddBanner} saveLabel={isSavingBanner ? 'Publishing...' : 'Publish Banner'} disabled={isSavingBanner} />
        </Modal>
      )}

      {/* ── ADD CATEGORY MODAL ── */}
      {isCategoryModalOpen && (
        <Modal onClose={() => setIsCategoryModalOpen(false)} title="New Category">
          <CategoryForm data={newCategory} setData={setNewCategory} />
          <ModalFooter onCancel={() => setIsCategoryModalOpen(false)} onSave={handleAddCategory} saveLabel={isSavingCategory ? 'Saving...' : 'Save Category'} disabled={isSavingCategory} />
        </Modal>
      )}

      {/* ── EDIT CATEGORY MODAL ── */}
      {isEditCategoryOpen && editingCategory && (
        <Modal onClose={() => setIsEditCategoryOpen(false)} title="Edit Category">
          <CategoryForm data={editingCategory} setData={setEditingCategory} />
          <ModalFooter onCancel={() => { setIsEditCategoryOpen(false); setEditingCategory(null); }} onSave={handleUpdateCategory} saveLabel={isSavingCategory ? 'Saving...' : 'Update Category'} disabled={isSavingCategory} />
        </Modal>
      )}

      {/* ── EDIT BANNER MODAL ── */}
      {isEditBannerOpen && editingBanner && (
        <Modal onClose={() => { setIsEditBannerOpen(false); setEditingBanner(null); }} title="Edit Banner">
          <BannerForm data={editingBanner} setData={setEditingBanner} categories={categories} />
          <ModalFooter onCancel={() => { setIsEditBannerOpen(false); setEditingBanner(null); }} onSave={handleUpdateBanner} saveLabel={isSavingBanner ? 'Updating...' : 'Update Banner'} disabled={isSavingBanner} />
        </Modal>
      )}

      {/* ── ADD USER MODAL ── */}
      {isUserModalOpen && (
        <Modal onClose={() => !isSendingInvite && setIsUserModalOpen(false)} title="Invite Staff Member">
          <UserForm data={newUser} setData={setNewUser} />
          <ModalFooter
            onCancel={() => setIsUserModalOpen(false)}
            onSave={handleAddUser}
            disabled={isSendingInvite}
            saveLabel={
              isSendingInvite
                ? <span className="flex items-center gap-2">
                    <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending…
                  </span>
                : '✉️ Send Invite'
            }
          />
        </Modal>
      )}

      {/* ── EDIT USER MODAL ── */}
      {isEditUserOpen && editingUser && (
        <Modal onClose={() => { setIsEditUserOpen(false); setEditingUser(null); }} title="Edit Staff Member">
          <UserForm data={editingUser} setData={setEditingUser} />
          <ModalFooter onCancel={() => { setIsEditUserOpen(false); setEditingUser(null); }} onSave={handleUpdateUser} saveLabel="Update Staff" />
        </Modal>
      )}

      {/* ── ADD SLIDE MODAL ── */}
      {isSlideModalOpen && (
        <Modal onClose={() => setIsSlideModalOpen(false)} title="Add Hero Slide">
          <SlideForm data={newSlide} setData={setNewSlide} categories={categories} />
          <ModalFooter onCancel={() => setIsSlideModalOpen(false)} onSave={handleAddSlide} saveLabel={isSavingSlide ? 'Saving...' : 'Add Slide'} />
        </Modal>
      )}

      {/* ── EDIT SLIDE MODAL ── */}
      {isEditSlideOpen && editingSlide && (
        <Modal onClose={() => { setIsEditSlideOpen(false); setEditingSlide(null); }} title="Edit Hero Slide">
          <SlideForm data={editingSlide} setData={setEditingSlide} categories={categories} />
          <ModalFooter onCancel={() => { setIsEditSlideOpen(false); setEditingSlide(null); }} onSave={handleUpdateSlide} saveLabel={isSavingSlide ? 'Saving...' : 'Update Slide'} />
        </Modal>
      )}

      {/* ── FULLSCREEN IMAGE VIEWER ── */}
      {fullscreenImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={() => setFullscreenImage(null)}>
          <button onClick={() => setFullscreenImage(null)} className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img src={fullscreenImage} alt="Fullscreen View" className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

/* ════════════════════ SUB-COMPONENTS ════════════════════ */

function StatCard({ label, value, icon, highlight, color }) {
  const valueClass = highlight ? 'text-[#F47B20]' : color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-500' : 'text-gray-800';
  return (
    <div className="bg-white p-5 rounded-2xl border border-[#E8E0D8] shadow-sm flex items-center justify-between">
      <div>
        <div className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">{label}</div>
        <div className={`text-2xl font-black ${valueClass}`}>{value}</div>
      </div>
      <div className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center text-xl border border-gray-100">{icon}</div>
    </div>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
      <h2 className="font-bold text-sm text-gray-800">{title}</h2>
      {children}
    </div>
  );
}

function Modal({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 bg-black/55 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-lg text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, onSave, saveLabel = 'Save', disabled = false, loading = false }) {
  return (
    <div className="flex justify-end gap-2.5 pt-2">
      <button onClick={onCancel} disabled={disabled || loading} className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200 disabled:opacity-50">Cancel</button>
      <button onClick={onSave} disabled={disabled || loading} className="px-5 py-2.5 bg-[#F47B20] text-white rounded-xl text-sm font-bold hover:bg-[#D96A10] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2">
        {loading && (
          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        )}
        {loading ? 'Saving...' : saveLabel}
      </button>
    </div>
  );
}

function InfoField({ label, value, className = '' }) {
  return (
    <div className={`space-y-0.5 ${className}`}>
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</div>
      <div className="text-sm text-gray-800 font-medium">{value || '—'}</div>
    </div>
  );
}

function ProductForm({ data, setData, categories = [] }) {
  const mediaList = data.mediaList || data.images || (data.image ? [data.image] : []);
  const [sizesInput, setSizesInput] = useState(data.sizes ? data.sizes.join(', ') : '');
  const [colorsInput, setColorsInput] = useState(data.colors ? data.colors.join(', ') : '');

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setData({ ...data, mediaList: [...mediaList, ...newFiles] });
    }
  };

  const removeMedia = (idx) => {
    const newList = [...mediaList];
    newList.splice(idx, 1);
    setData({ ...data, mediaList: newList });
  };

  const moveMediaLeft = (idx) => {
    if (idx === 0) return;
    const newList = [...mediaList];
    const temp = newList[idx - 1];
    newList[idx - 1] = newList[idx];
    newList[idx] = temp;
    setData({ ...data, mediaList: newList });
  };

  const moveMediaRight = (idx) => {
    if (idx === mediaList.length - 1) return;
    const newList = [...mediaList];
    const temp = newList[idx + 1];
    newList[idx + 1] = newList[idx];
    newList[idx] = temp;
    setData({ ...data, mediaList: newList });
  };

  const setAsCover = (idx) => {
    if (idx === 0) return;
    const newList = [...mediaList];
    const [selected] = newList.splice(idx, 1);
    newList.unshift(selected);
    setData({ ...data, mediaList: newList });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Product Name">
          <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} placeholder="e.g. Satin Ballgown Gown" />
        </FormField>
        <FormField label="Category">
          <select className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#F47B20]" value={data.category_id || ''} onChange={e => setData({ ...data, category_id: e.target.value })}>
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Price (₦)">
          <input type="number" className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.price} onChange={e => setData({ ...data, price: e.target.value })} placeholder="e.g. 450000" />
        </FormField>
        <FormField label="Original Price (N) — slash price">
          <input type="number" className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.original_price || ''} onChange={e => setData({ ...data, original_price: e.target.value })} placeholder="e.g. 600000 (optional)" />
        </FormField>
        <FormField label="Stock">
          <input type="number" className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.stock} onChange={e => setData({ ...data, stock: e.target.value })} placeholder="e.g. 10" />
        </FormField>
        <FormField label="Sizes (Comma separated)">
          <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={sizesInput} onChange={e => {
            setSizesInput(e.target.value);
            setData({ ...data, sizes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) });
          }} placeholder="e.g. S, M, L, XL" />
        </FormField>
        <FormField label="Colors (Comma separated)">
          <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={colorsInput} onChange={e => {
            setColorsInput(e.target.value);
            setData({ ...data, colors: e.target.value.split(',').map(s => s.trim()).filter(Boolean) });
          }} placeholder="e.g. Red, Blue, Green" />
        </FormField>
        <FormField label="Status">
          <select className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#F47B20]" value={data.status} onChange={e => setData({ ...data, status: e.target.value })}>
            <option value="active">Active</option><option value="draft">Draft</option>
          </select>
        </FormField>
        <div className="flex flex-col justify-center pt-2">
          <label className="flex items-center gap-2 cursor-pointer" onClick={() => setData({ ...data, is_free_shipping: !data.is_free_shipping })}>
            <div className={`relative w-10 h-5 rounded-full transition-colors ${data.is_free_shipping ? 'bg-[#F47B20]' : 'bg-gray-300'}`}>
              <div className={`absolute top-1 left-1 w-3 h-3 bg-white rounded-full transition-transform ${data.is_free_shipping ? 'translate-x-5' : ''}`} />
            </div>
            <span className="text-sm font-bold text-[#1A1A2E]">Free Shipping</span>
          </label>
          <span className="text-xs text-gray-500 mt-1">If enabled, this product ships for free.</span>
        </div>
      </div>
      <FormField label="Product Images (Reorder with arrows, first is Cover)">
        <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-orange-50 hover:border-[#F47B20] transition-colors">
          <span className="text-xs text-gray-500 font-medium">Click to upload images or videos</span>
          <span className="text-[10px] text-gray-400 mt-0.5">You can add multiple files at once</span>
          <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileChange} />
        </label>
      </FormField>

      {mediaList.length > 0 && (
        <div className="space-y-1">
          <div className="flex gap-2 flex-wrap">
            {mediaList.map((item, i) => {
              const src = item instanceof File ? URL.createObjectURL(item) : (item.url || item);
              const isVideo = item instanceof File ? item.type.startsWith('video/') : src.match(/\.(mp4|webm|ogg)$/i);
              return (
                <div key={i} className={`w-24 h-24 rounded-xl overflow-hidden border-2 relative group ${i === 0 ? 'border-[#F47B20] shadow-md' : 'border-gray-200'}`}>
                  {isVideo ? (
                    <video src={src} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={src} alt={`preview-${i}`} className="w-full h-full object-cover" />
                  )}
                  
                  {/* Remove button */}
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeMedia(i); }} className="absolute top-1 right-1 w-5 h-5 bg-black/60 hover:bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">✕</button>
                  
                  {/* Reorder buttons */}
                  <div className="absolute inset-x-0 bottom-0 bg-black/50 p-1 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveMediaLeft(i); }} disabled={i === 0} className="text-white hover:text-[#F47B20] disabled:opacity-30 px-1">◀</button>
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAsCover(i); }} disabled={i === 0} className="text-[9px] text-white hover:text-[#F47B20] font-bold px-1">COVER</button>
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveMediaRight(i); }} disabled={i === mediaList.length - 1} className="text-white hover:text-[#F47B20] disabled:opacity-30 px-1">▶</button>
                  </div>

                  {i === 0 && <div className="absolute top-0 left-0 bg-[#F47B20] text-white text-[8px] px-1 font-bold z-10">COVER</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <FormField label="Description">
        <textarea rows={4} className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none outline-none focus:border-[#F47B20]" value={data.description || ''} onChange={e => setData({ ...data, description: e.target.value })} placeholder="Describe the product — fabric, fit, care instructions..." />
      </FormField>
    </div>
  );
}

function BannerForm({ data, setData, categories = [] }) {
  const preview = data.imageFile
    ? URL.createObjectURL(data.imageFile)
    : data.image || null;

  return (
    <div className="space-y-4">
      <FormField label="Banner Title">
        <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.title} onChange={e => setData({ ...data, title: e.target.value })} placeholder="e.g. Summer Bridal Sale" />
      </FormField>
      <FormField label="Subtitle">
        <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.subtitle} onChange={e => setData({ ...data, subtitle: e.target.value })} placeholder="e.g. Up to 30% off accessories" />
      </FormField>
      <FormField label="Banner Image">
        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-orange-50 hover:border-[#F47B20] transition-colors">
          <span className="text-2xl mb-1"></span>
          <span className="text-xs text-gray-500 font-medium">Click to upload banner image</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => setData({ ...data, imageFile: e.target.files[0] })}
          />
        </label>
      </FormField>
      {preview && (
        <div className="rounded-xl overflow-hidden h-32 border border-gray-200">
          <img src={preview} alt="preview" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-6">
        <FormField label="Start Date">
          <input type="date" className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.start} onChange={e => setData({ ...data, start: e.target.value })} />
        </FormField>
        <FormField label="End Date">
          <input type="date" className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.end} onChange={e => setData({ ...data, end: e.target.value })} />
        </FormField>
      </div>
      <FormField label="Status">
        <select className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#F47B20]" value={data.status} onChange={e => setData({ ...data, status: e.target.value })}>
          <option value="active">Active</option><option value="draft">Draft</option>
        </select>
      </FormField>
      <FormField label="Link to Category (Optional)">
        <select className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#F47B20]" value={data.cta || ''} onChange={e => setData({ ...data, cta: e.target.value })}>
          <option value="">None (Just an image)</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.name}>{cat.name}</option>
          ))}
        </select>
        <div className="text-[10px] text-gray-400 mt-1">If selected, clicking the banner will take the user to this category.</div>
      </FormField>
    </div>
  );
}

function CategoryForm({ data, setData }) {
  const preview = data.imageFile
    ? URL.createObjectURL(data.imageFile)
    : data.imageUrl || null;

  return (
    <div className="space-y-4">
      <FormField label="Category Name">
        <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} placeholder="e.g. Gowns" />
      </FormField>
      <FormField label="Category Image (Square 500x500)">
        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-orange-50 hover:border-[#F47B20] transition-colors">
          <span className="text-2xl mb-1"></span>
          <span className="text-xs text-gray-500 font-medium">Click to upload image</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => setData({ ...data, imageFile: e.target.files[0] })}
          />
        </label>
      </FormField>
      {preview && (
        <div className="rounded-xl overflow-hidden aspect-square w-32 border border-gray-200">
          <img src={preview} alt="preview" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}

function UserForm({ data, setData }) {
  return (
    <div className="space-y-4">
      <FormField label="Full Name">
        <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} placeholder="e.g. Chioma Okafor" />
      </FormField>
      <FormField label="Email">
        <input className="w-full p-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#F47B20]" value={data.email} onChange={e => setData({ ...data, email: e.target.value })} placeholder="e.g. chioma@mannabridal.com" />
      </FormField>
      <FormField label="Assign Role">
        <select className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#F47B20]" value={data.role} onChange={e => setData({ ...data, role: e.target.value })}>
          <option value="superadmin"> Super Admin (Owner)</option>
          <option value="manager"> Manager</option>
          <option value="staff"> Sales Staff</option>
          <option value="inventory"> Inventory Staff</option>
        </select>
      </FormField>
    </div>
  );
}

function SlideForm({ data, setData, categories = [] }) {
  const preview = data.imageFile
    ? URL.createObjectURL(data.imageFile)
    : data.image || null;

  return (
    <div className="space-y-4">
      <FormField label="Slide Image (1920x1080)">
        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-orange-50 hover:border-[#F47B20] transition-colors">
          <span className="text-2xl mb-1"></span>
          <span className="text-xs text-gray-500 font-medium">Click to upload slide image</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => setData({ ...data, imageFile: e.target.files[0] })}
          />
        </label>
      </FormField>
      {preview && (
        <div className="rounded-xl overflow-hidden h-28 border border-gray-200">
          <img src={preview} alt="preview" className="w-full h-full object-cover" />
        </div>
      )}
      <FormField label="Link to Category (Optional)">
        <select className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#F47B20]" value={data.cta || ''} onChange={e => setData({ ...data, cta: e.target.value })}>
          <option value="">None (Just an image)</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.name}>{cat.name}</option>
          ))}
        </select>
        <div className="text-[10px] text-gray-400 mt-1">If selected, clicking the slide will take the user to this category.</div>
      </FormField>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block">{label}</label>
      {children}
    </div>
  );
}
